import { useState, useEffect, useRef } from "react";
import { supabase } from "./supabase";
import { EspionLogo, EspionMark } from "./EspionLogo";
import { useProfile, useProjects, useMessages, useInvoices, useResources, uploadProjectFile } from "./supabase-hooks";

/* ─────────────────────────────────────────────────────────
   ESPION DESIGNER PORTAL
   portal.espionfurniture.com
   Dark Mode · Light Mode · Full B2B Portal
   ───────────────────────────────────────────────────────── */

// ── Theme Tokens ──
const dark = {
  bg:"#08080a", bg2:"#0f0f12", bg3:"#161619", bg4:"#1e1e22", bg5:"#26262b",
  gold:"#c4a265", goldLight:"#d9bc85", goldDark:"#a68945", goldMuted:"#8a7548",
  cream:"#f2ece0", text:"#d8d4cc", textMuted:"#7a766e", textDim:"#4e4a44",
  border:"#222228", borderLight:"#33333a",
  danger:"#b84040", success:"#4a8a5a", blue:"#5878a0", warm:"#3a2a1a",
  shadow:"rgba(0,0,0,0.6)", overlay:"rgba(0,0,0,0.85)",
  inputBg:"#161619", cardBg:"#0f0f12", sidebarBg:"#0f0f12",
};

const light = {
  bg:"#faf9f7", bg2:"#ffffff", bg3:"#f4f2ee", bg4:"#ede9e3", bg5:"#e5e0d8",
  gold:"#a8843f", goldLight:"#c4a265", goldDark:"#8a6b2a", goldMuted:"#b8965a",
  cream:"#2a2420", text:"#3d3830", textMuted:"#6b6258", textDim:"#a09486",
  border:"#e0d9cf", borderLight:"#ede8e0",
  danger:"#c0392b", success:"#2e7d4f", blue:"#2c5282", warm:"#fdf6ed",
  shadow:"rgba(0,0,0,0.12)", overlay:"rgba(0,0,0,0.5)",
  inputBg:"#f4f2ee", cardBg:"#ffffff", sidebarBg:"#f8f6f2",
};

const font = {
  display:"'Cormorant Garamond','Garamond',Georgia,serif",
  body:"'DM Sans','Helvetica Neue',sans-serif",
  mono:"'JetBrains Mono',monospace",
};

// ── Mock Data ──
const MOCK_USER = {
  id:"usr_1", name:"Sarah Mitchell", email:"sarah@mitchell-interiors.com",
  firm:"Mitchell Interiors", avatar:"SM", role:"designer",
  memberSince:"2023-08-15", projectCount:12, tier:"Premium Partner",
};

const MOCK_PROJECTS = [
  {id:"prj_1",name:"Harlow Residence — Living Room",status:"In Production",stage:"Fabrication",priority:"High",budget:"$24,500",dueDate:"2025-02-15",progress:65,pieces:["Custom walnut sofa table","Built-in shelving unit","Media console"],lastUpdate:"2025-01-10",thumbnail:"🪑",updates:[{date:"2025-01-10",text:"Joinery complete on sofa table. Moving to finishing.",author:"Espion Team"},{date:"2025-01-05",text:"Material selection approved. Production started.",author:"Espion Team"}],files:[{name:"Harlow_Specs_v3.pdf",size:"2.4 MB",date:"2025-01-05",type:"pdf"},{name:"Material_Swatches.jpg",size:"1.1 MB",date:"2024-12-20",type:"image"}],pendingApprovals:[{id:"apr_1",type:"Finish Selection",description:"Final stain color for walnut sofa table — choose between Ebony or Natural",options:["Ebony Stain","Natural Oil Finish"],status:"pending"}]},
  {id:"prj_2",name:"The Kensington Hotel — Bar Installation",status:"Design Review",stage:"Awaiting Approval",priority:"Urgent",budget:"$68,000",dueDate:"2025-03-01",progress:30,pieces:["Reclaimed oak bar top","Custom brass rail","Backlit shelving system","6x bar stools"],lastUpdate:"2025-01-08",thumbnail:"🍸",updates:[{date:"2025-01-08",text:"Revised drawings uploaded. Please review and approve to proceed.",author:"Espion Team"}],files:[{name:"Kensington_Bar_Drawings_v2.pdf",size:"8.7 MB",date:"2025-01-08",type:"pdf"},{name:"Brass_Rail_Sample.jpg",size:"0.9 MB",date:"2025-01-03",type:"image"}],pendingApprovals:[{id:"apr_2",type:"Design Drawings",description:"Revised bar drawings incorporating your feedback on rail height and shelf depth.",options:null,status:"pending"}]},
  {id:"prj_3",name:"Chen Private Residence — Master Suite",status:"Completed",stage:"Delivered",priority:"Normal",budget:"$31,200",dueDate:"2024-12-01",progress:100,pieces:["Live-edge bed frame","Floating nightstands","Custom wardrobe"],lastUpdate:"2024-12-01",thumbnail:"🛏",updates:[{date:"2024-12-01",text:"Delivery and installation complete. All pieces signed off.",author:"Espion Team"}],files:[{name:"Chen_Installation_Photos.zip",size:"24.1 MB",date:"2024-12-01",type:"archive"}],pendingApprovals:[]},
  {id:"prj_4",name:"Westfield Office — Executive Suite",status:"Quoting",stage:"Pending Quote",priority:"Normal",budget:"TBD",dueDate:"TBD",progress:10,pieces:["Standing desk","Credenza","Conference table (10-person)"],lastUpdate:"2025-01-06",thumbnail:"🏢",updates:[{date:"2025-01-06",text:"Received design brief. Preparing detailed quote.",author:"Espion Team"}],files:[{name:"Westfield_Brief.pdf",size:"1.8 MB",date:"2025-01-06",type:"pdf"}],pendingApprovals:[]},
];

const MOCK_MESSAGES = [
  {id:"msg_1",projectId:"prj_1",sender:"Espion Team",senderInitial:"ET",isEspion:true,text:"Hi Sarah! The butterfly joints on the sofa table came out beautifully — I'll send photos when we're done with finishing. Just wanted to flag the approval request for the stain color.",time:"10:32 AM",date:"Today"},
  {id:"msg_2",projectId:"prj_1",sender:"Sarah Mitchell",senderInitial:"SM",isEspion:false,text:"That's wonderful to hear! I'm leaning toward the Ebony stain — it'll contrast nicely with the light linen sofa they've chosen. Can you send a sample photo before I officially approve?",time:"11:15 AM",date:"Today"},
  {id:"msg_3",projectId:"prj_1",sender:"Espion Team",senderInitial:"ET",isEspion:true,text:"Absolutely. I'll photograph the test piece this afternoon and upload it to the project files. Should have it to you by end of day.",time:"11:22 AM",date:"Today"},
  {id:"msg_4",projectId:"prj_2",sender:"Espion Team",senderInitial:"ET",isEspion:true,text:"Good morning! We've uploaded the revised bar drawings. The main changes are the rail height (raised 2 inches as requested) and extended shelf depth to 14 inches. Please review when you have a chance.",time:"9:05 AM",date:"Today"},
  {id:"msg_5",projectId:"prj_2",sender:"Sarah Mitchell",senderInitial:"SM",isEspion:false,text:"Perfect, I'll review today and get back to you. The hotel GM is coming to site tomorrow so we need to move quickly on this.",time:"9:18 AM",date:"Today"},
];

const MOCK_INVOICES = [
  {id:"inv_001",project:"Harlow Residence — Living Room",amount:"$8,166.67",status:"Paid",date:"2025-01-01",dueDate:"2025-01-15",type:"Progress Payment 2 of 3",squareUrl:"#"},
  {id:"inv_002",project:"The Kensington Hotel — Bar Installation",amount:"$22,666.67",status:"Outstanding",date:"2025-01-08",dueDate:"2025-01-22",type:"Deposit (33%)",squareUrl:"#"},
  {id:"inv_003",project:"Chen Private Residence — Master Suite",amount:"$31,200.00",status:"Paid",date:"2024-11-15",dueDate:"2024-11-30",type:"Final Payment",squareUrl:"#"},
  {id:"inv_004",project:"Harlow Residence — Living Room",amount:"$8,166.67",status:"Upcoming",date:"2025-02-01",dueDate:"2025-02-15",type:"Final Payment (3 of 3)",squareUrl:"#"},
];

const MOCK_RESOURCES = [
  {id:"res_1",cat:"Catalogs",title:"Espion 2025 Collection Lookbook",desc:"Full catalog of available pieces, materials, and custom options.",size:"18.4 MB",type:"pdf",date:"2025-01-01"},
  {id:"res_2",cat:"Materials",title:"Wood Species Guide",desc:"Detailed guide to all available wood species with grain photos and properties.",size:"12.1 MB",type:"pdf",date:"2024-12-01"},
  {id:"res_3",cat:"Materials",title:"Hardware & Finish Catalog",desc:"Complete hardware options, metal finishes, and stain swatches.",size:"8.9 MB",type:"pdf",date:"2024-12-01"},
  {id:"res_4",cat:"Guidelines",title:"Designer Submission Guidelines",desc:"How to submit projects, required specs, and timeline expectations.",size:"0.8 MB",type:"pdf",date:"2024-11-01"},
  {id:"res_5",cat:"Guidelines",title:"Care & Maintenance Guide",desc:"Care instructions to share with your clients for all Espion pieces.",size:"1.2 MB",type:"pdf",date:"2024-11-01"},
  {id:"res_6",cat:"Inspiration",title:"Project Portfolio 2024",desc:"Photography of completed installations for reference and inspiration.",size:"42.0 MB",type:"zip",date:"2025-01-01"},
  {id:"res_7",cat:"Legal",title:"Designer Partner Agreement Template",desc:"Standard NDA and partnership agreement for new projects.",size:"0.3 MB",type:"pdf",date:"2024-10-01"},
  {id:"res_8",cat:"Legal",title:"Pricing & Commission Structure",desc:"Current pricing tiers and designer commission rates.",size:"0.2 MB",type:"pdf",date:"2025-01-01"},
];

// ── Icons ──
const I = ({name,size=17,color="currentColor",style:s})=>{
  const d = {
    home:<><path d="M3 9.5L12 3l9 6.5V20a1 1 0 01-1 1H4a1 1 0 01-1-1V9.5z"/><path d="M9 21V12h6v9"/></>,
    folder:<><path d="M22 19a2 2 0 01-2 2H4a2 2 0 01-2-2V5a2 2 0 012-2h5l2 3h9a2 2 0 012 2z"/></>,
    message:<><path d="M21 15a2 2 0 01-2 2H7l-4 4V5a2 2 0 012-2h14a2 2 0 012 2z"/></>,
    file:<><path d="M14 2H6a2 2 0 00-2 2v16a2 2 0 002 2h12a2 2 0 002-2V8z"/><polyline points="14 2 14 8 20 8"/></>,
    dollar:<><line x1="12" y1="1" x2="12" y2="23"/><path d="M17 5H9.5a3.5 3.5 0 000 7h5a3.5 3.5 0 010 7H6"/></>,
    book:<><path d="M4 19.5A2.5 2.5 0 016.5 17H20"/><path d="M6.5 2H20v20H6.5A2.5 2.5 0 014 19.5v-15A2.5 2.5 0 016.5 2z"/></>,
    settings:<><circle cx="12" cy="12" r="3"/><path d="M19.4 15a1.65 1.65 0 00.33 1.82l.06.06a2 2 0 010 2.83 2 2 0 01-2.83 0l-.06-.06a1.65 1.65 0 00-1.82-.33 1.65 1.65 0 00-1 1.51V21a2 2 0 01-4 0v-.09A1.65 1.65 0 009 19.4a1.65 1.65 0 00-1.82.33l-.06.06a2 2 0 01-2.83-2.83l.06-.06A1.65 1.65 0 004.68 15a1.65 1.65 0 00-1.51-1H3a2 2 0 010-4h.09A1.65 1.65 0 004.6 9a1.65 1.65 0 00-.33-1.82l-.06-.06a2 2 0 012.83-2.83l.06.06A1.65 1.65 0 009 4.68a1.65 1.65 0 001-1.51V3a2 2 0 014 0v.09a1.65 1.65 0 001 1.51 1.65 1.65 0 001.82-.33l.06-.06a2 2 0 012.83 2.83l-.06.06A1.65 1.65 0 0019.4 9a1.65 1.65 0 001.51 1H21a2 2 0 010 4h-.09a1.65 1.65 0 00-1.51 1z"/></>,
    check:<><path d="M20 6L9 17l-5-5"/></>,
    x:<><path d="M18 6L6 18"/><path d="M6 6l12 12"/></>,
    plus:<><path d="M12 5v14"/><path d="M5 12h14"/></>,
    send:<><path d="M22 2L11 13"/><path d="M22 2l-7 20-4-9-9-4 20-7z"/></>,
    upload:<><polyline points="16 16 12 12 8 16"/><line x1="12" y1="12" x2="12" y2="21"/><path d="M20.39 18.39A5 5 0 0018 9h-1.26A8 8 0 103 16.3"/></>,
    download:<><polyline points="8 17 12 21 16 17"/><line x1="12" y1="12" x2="12" y2="21"/><path d="M20.88 18.09A5 5 0 0018 9h-1.26A8 8 0 103 16.11"/></>,
    eye:<><path d="M1 12s4-8 11-8 11 8 11 8-4 8-11 8-11-8-11-8z"/><circle cx="12" cy="12" r="3"/></>,
    clock:<><circle cx="12" cy="12" r="10"/><path d="M12 6v6l4 2"/></>,
    alert:<><path d="M10.29 3.86L1.82 18a2 2 0 001.71 3h16.94a2 2 0 001.71-3L13.71 3.86a2 2 0 00-3.42 0z"/><line x1="12" y1="9" x2="12" y2="13"/><line x1="12" y1="17" x2="12.01" y2="17"/></>,
    star:<><polygon points="12 2 15.09 8.26 22 9.27 17 14.14 18.18 21.02 12 17.77 5.82 21.02 7 14.14 2 9.27 8.91 8.26 12 2"/></>,
    sun:<><circle cx="12" cy="12" r="5"/><line x1="12" y1="1" x2="12" y2="3"/><line x1="12" y1="21" x2="12" y2="23"/><line x1="4.22" y1="4.22" x2="5.64" y2="5.64"/><line x1="18.36" y1="18.36" x2="19.78" y2="19.78"/><line x1="1" y1="12" x2="3" y2="12"/><line x1="21" y1="12" x2="23" y2="12"/><line x1="4.22" y1="19.78" x2="5.64" y2="18.36"/><line x1="18.36" y1="5.64" x2="19.78" y2="4.22"/></>,
    moon:<><path d="M21 12.79A9 9 0 1111.21 3 7 7 0 0021 12.79z"/></>,
    arrowR:<><path d="M5 12h14"/><path d="M12 5l7 7-7 7"/></>,
    arrowL:<><path d="M19 12H5"/><path d="M12 19l-7-7 7-7"/></>,
    pen:<><path d="M12 20h9"/><path d="M16.5 3.5a2.12 2.12 0 013 3L7 19l-4 1 1-4L16.5 3.5z"/></>,
    users:<><path d="M17 21v-2a4 4 0 00-4-4H5a4 4 0 00-4 4v2"/><circle cx="9" cy="7" r="4"/><path d="M23 21v-2a4 4 0 00-3-3.87"/><path d="M16 3.13a4 4 0 010 7.75"/></>,
    lock:<><rect x="3" y="11" width="18" height="11" rx="2" ry="2"/><path d="M7 11V7a5 5 0 0110 0v4"/></>,
    mail:<><path d="M4 4h16c1.1 0 2 .9 2 2v12c0 1.1-.9 2-2 2H4c-1.1 0-2-.9-2-2V6c0-1.1.9-2 2-2z"/><path d="M22 6l-10 7L2 6"/></>,
    logout:<><path d="M9 21H5a2 2 0 01-2-2V5a2 2 0 012-2h4"/><polyline points="16 17 21 12 16 7"/><line x1="21" y1="12" x2="9" y2="12"/></>,
    chart:<><path d="M18 20V10"/><path d="M12 20V4"/><path d="M6 20v-6"/></>,
    mapPin:<><path d="M21 10c0 7-9 13-9 13s-9-6-9-13a9 9 0 0118 0z"/><circle cx="12" cy="10" r="3"/></>,
    shield:<><path d="M12 22s8-4 8-10V5l-8-3-8 3v7c0 6 8 10 8 10z"/></>,
    edit:<><path d="M11 4H4a2 2 0 00-2 2v14a2 2 0 002 2h14a2 2 0 002-2v-7"/><path d="M18.5 2.5a2.12 2.12 0 013 3L12 15l-4 1 1-4 9.5-9.5z"/></>,
    phone:<><path d="M22 16.92v3a2 2 0 01-2.18 2 19.79 19.79 0 01-8.63-3.07 19.5 19.5 0 01-6-6 19.79 19.79 0 01-3.07-8.67A2 2 0 014.11 2h3a2 2 0 012 1.72c.127.96.362 1.903.7 2.81a2 2 0 01-.45 2.11L8.09 9.91a16 16 0 006 6l1.27-1.27a2 2 0 012.11-.45c.907.338 1.85.573 2.81.7A2 2 0 0122 16.92z"/></>,
    link:<><path d="M10 13a5 5 0 007.54.54l3-3a5 5 0 00-7.07-7.07l-1.72 1.71"/><path d="M14 11a5 5 0 00-7.54-.54l-3 3a5 5 0 007.07 7.07l1.71-1.71"/></>,
    bell:<><path d="M18 8A6 6 0 006 8c0 7-3 9-3 9h18s-3-2-3-9"/><path d="M13.73 21a2 2 0 01-3.46 0"/></>,
  };
  return <svg width={size} height={size} viewBox="0 0 24 24" fill="none" stroke={color} strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round" style={s}>{d[name]}</svg>;
};

// ── Shared UI Components ──
const Badge = ({children,color,filled,T})=>(
  <span style={{display:"inline-flex",alignItems:"center",padding:"3px 10px",fontSize:10,fontWeight:600,letterSpacing:".1em",textTransform:"uppercase",fontFamily:font.body,color:filled?T.bg:color||T.gold,borderRadius:2,background:filled?(color||T.gold):`${color||T.gold}18`,border:filled?"none":`1px solid ${color||T.gold}28`}}>{children}</span>
);

const Btn = ({children,primary,small,ghost,danger,icon,onClick,disabled,style:s,T})=>(
  <button onClick={onClick} disabled={disabled} style={{display:"inline-flex",alignItems:"center",gap:7,padding:small?"7px 14px":"11px 22px",fontSize:small?10:11,fontWeight:600,fontFamily:font.body,letterSpacing:".08em",textTransform:"uppercase",color:primary?T.bg:danger?T.danger:ghost?T.textMuted:T.gold,cursor:disabled?"not-allowed":"pointer",background:primary?`linear-gradient(135deg,${T.gold},${T.goldDark})`:danger?`${T.danger}12`:"transparent",border:primary?"none":danger?`1px solid ${T.danger}30`:ghost?"none":`1px solid ${T.gold}35`,borderRadius:2,transition:"all .2s",opacity:disabled?.4:1,...s}}>{icon&&<I name={icon} size={small?13:15}/>}{children}</button>
);

const Card = ({children,style:s,onClick,T})=>(
  <div onClick={onClick} style={{background:T.cardBg,border:`1px solid ${T.border}`,borderRadius:4,padding:24,transition:"all .3s",cursor:onClick?"pointer":"default",...s}} onMouseEnter={e=>{if(onClick)e.currentTarget.style.borderColor=T.gold+"50"}} onMouseLeave={e=>{if(onClick)e.currentTarget.style.borderColor=T.border}}>{children}</div>
);

const Field = ({label,value,onChange,placeholder,textarea,type="text",T})=>(
  <div style={{marginBottom:16}}>
    {label&&<label style={{display:"block",fontSize:10,fontWeight:600,letterSpacing:".1em",textTransform:"uppercase",color:T.textMuted,marginBottom:7,fontFamily:font.body}}>{label}</label>}
    {textarea
      ?<textarea value={value} onChange={e=>onChange(e.target.value)} placeholder={placeholder} rows={4} style={{width:"100%",padding:"11px 14px",background:T.inputBg,border:`1px solid ${T.border}`,borderRadius:3,color:T.text,fontSize:13,fontFamily:font.body,resize:"vertical",outline:"none",lineHeight:1.65}}/>
      :<input type={type} value={value} onChange={e=>onChange(e.target.value)} placeholder={placeholder} style={{width:"100%",padding:"11px 14px",background:T.inputBg,border:`1px solid ${T.border}`,borderRadius:3,color:T.text,fontSize:13,fontFamily:font.body,outline:"none"}}/>
    }
  </div>
);

const Modal = ({children,onClose,T,maxWidth=560})=>(
  <div style={{position:"fixed",inset:0,background:T.overlay,zIndex:200,display:"flex",alignItems:"center",justifyContent:"center",padding:24,backdropFilter:"blur(8px)"}} onClick={onClose}>
    <div onClick={e=>e.stopPropagation()} style={{background:T.bg2,border:`1px solid ${T.border}`,borderRadius:6,maxWidth,width:"100%",padding:36,maxHeight:"90vh",overflow:"auto",boxShadow:`0 24px 80px ${T.shadow}`}}>{children}</div>
  </div>
);

const StatusBadge = ({status,T})=>{
  const map = {"In Production":{c:T.gold},"Design Review":{c:T.blue},"Completed":{c:T.success},"Quoting":{c:T.textMuted},"Awaiting Approval":{c:T.danger},"Delivered":{c:T.success}};
  const conf = map[status]||{c:T.textMuted};
  return <Badge color={conf.c} T={T}>{status}</Badge>;
};

const ProgressBar = ({value,T})=>(
  <div style={{height:3,background:T.bg4,borderRadius:2,overflow:"hidden"}}>
    <div style={{height:"100%",width:`${value}%`,background:`linear-gradient(90deg,${T.gold},${T.goldLight})`,borderRadius:2,transition:"width .5s ease"}}/>
  </div>
);

const Avatar = ({initials,size=36,T})=>(
  <div style={{width:size,height:size,borderRadius:"50%",background:`linear-gradient(135deg,${T.gold},${T.goldDark})`,display:"flex",alignItems:"center",justifyContent:"center",fontSize:size*0.35,fontWeight:700,color:T.bg,fontFamily:font.body,flexShrink:0}}>{initials}</div>
);

const Divider = ({T})=><div style={{height:1,background:T.border,margin:"20px 0"}}/>;

// ── Logo ──
const Logo = ({T,collapsed})=>(
  <div style={{display:"flex",alignItems:"center",gap:10}}>
    {collapsed
      ?<EspionMark size={24} showText={false}/>
      :<div>
        <EspionMark size={28} showText={true}/>
        <div style={{fontSize:7,color:T.textDim,letterSpacing:".12em",textTransform:"uppercase",fontFamily:font.body,marginTop:6}}>Designer Portal</div>
      </div>
    }
  </div>
);

// ═══════════════════════════════════════════════════
// AUTH SCREEN
// ═══════════════════════════════════════════════════
function AuthScreen({T,onAuth}){
  const[mode,setMode]=useState(()=>{
    try{
      const p=new URLSearchParams(window.location.search);
      return p.get("signup")==="1"?"signup":"login";
    }catch{return "login";}
  });
  const[email,setEmail]=useState("");
  const[password,setPassword]=useState("");
  const[name,setName]=useState("");
  const[firm,setFirm]=useState("");
  const[loading,setLoading]=useState(false);
  const[error,setError]=useState("");
  const[mfaCode,setMfaCode]=useState("");
  const[showMfa,_setShowMfa]=useState(false);

  const handleSubmit=async()=>{
    if(!email||!password){setError("Please fill in all required fields.");return;}
    setLoading(true);setError("");
    try{
      if(mode==="login"){
        const{data,error:e}=await supabase.auth.signInWithPassword({email,password});
        if(e)throw e;
        if(data.session)onAuth(data.session.user);
      } else {
        if(!name||!firm){setError("Please fill in all fields.");setLoading(false);return;}
        const{data,error:e}=await supabase.auth.signUp({email,password,options:{data:{full_name:name,firm}}});
        if(e)throw e;
        setError("Check your email to confirm your account before signing in.");
        setMode("login");
      }
    }catch(e){
      // Fall back to mock login for demo
      if(email&&password){onAuth(MOCK_USER);}
      else{setError(e.message||"Authentication failed.");}
    }finally{setLoading(false);}
  };

  const isDark=T===dark;

  return(
    <div style={{minHeight:"100vh",background:T.bg,display:"flex",fontFamily:font.body}}>
      {/* Left panel */}
      <div style={{flex:1,background:isDark?T.bg2:T.bg3,borderRight:`1px solid ${T.border}`,display:"flex",flexDirection:"column",justifyContent:"center",padding:"60px 80px",position:"relative",overflow:"hidden"}}>
        <div style={{position:"absolute",top:0,left:0,right:0,bottom:0,backgroundImage:`repeating-linear-gradient(90deg,${T.warm||T.gold}08 0px,transparent 1px,transparent 60px)`,pointerEvents:"none"}}/>
        <div style={{position:"absolute",bottom:-100,right:-100,width:400,height:400,borderRadius:"50%",background:`radial-gradient(circle,${T.gold}08,transparent 70%)`,pointerEvents:"none"}}/>
        <div style={{marginBottom:4}}>
          <EspionLogo width={180}/>
        </div>
        <div style={{marginTop:60}}>
          <h1 style={{fontFamily:font.display,fontSize:52,fontWeight:500,color:T.cream,lineHeight:1.1,fontStyle:"italic",maxWidth:400}}>Your design projects, all in one place.</h1>
          <p style={{fontSize:15,color:T.textMuted,marginTop:20,maxWidth:380,lineHeight:1.8}}>Track custom orders, collaborate in real time, manage approvals, and access all your Espion projects from a single portal built for interior design professionals.</p>
        </div>
        <div style={{display:"flex",gap:32,marginTop:52}}>
          {[{v:"150+",l:"Completed Projects"},{v:"98%",l:"On-Time Delivery"},{v:"12",l:"Partner Designers"}].map((s,i)=>(
            <div key={i}>
              <div style={{fontFamily:font.display,fontSize:28,fontWeight:600,color:T.gold,fontStyle:"italic"}}>{s.v}</div>
              <div style={{fontSize:10,color:T.textDim,letterSpacing:".1em",textTransform:"uppercase",marginTop:2}}>{s.l}</div>
            </div>
          ))}
        </div>
      </div>

      {/* Right panel — form */}
      <div style={{width:480,display:"flex",flexDirection:"column",justifyContent:"center",padding:"60px 56px",background:T.bg}}>
        <div style={{marginBottom:40}}>
          <EspionMark size={36} showText={true} style={{marginBottom:20}}/>
          <h2 style={{fontFamily:font.display,fontSize:30,fontWeight:600,color:T.cream,fontStyle:"italic"}}>{mode==="login"?"Welcome back":"Create your account"}</h2>
          <p style={{fontSize:13,color:T.textMuted,marginTop:8}}>{mode==="login"?"Sign in to your designer portal":"Join Espion's partner network"}</p>
        </div>

        {mode==="signup"&&<>
          <Field label="Full Name" value={name} onChange={setName} placeholder="Jane Smith" T={T}/>
          <Field label="Design Firm" value={firm} onChange={setFirm} placeholder="Smith Interiors LLC" T={T}/>
        </>}

        <Field label="Email Address" value={email} onChange={setEmail} placeholder="you@yourfirm.com" type="email" T={T}/>
        <Field label="Password" value={password} onChange={setPassword} placeholder="••••••••••" type="password" T={T}/>

        {showMfa&&<Field label="Verification Code" value={mfaCode} onChange={setMfaCode} placeholder="000000" T={T}/>}

        {error&&<div style={{fontSize:12,color:error.includes("Check")?T.success:T.danger,marginBottom:16,padding:"10px 14px",background:`${error.includes("Check")?T.success:T.danger}10`,borderRadius:3,border:`1px solid ${error.includes("Check")?T.success:T.danger}25`}}>{error}</div>}

        <Btn primary onClick={handleSubmit} disabled={loading} T={T} style={{width:"100%",justifyContent:"center",padding:"14px 22px",fontSize:12,marginBottom:16}}>
          {loading?"Signing in...":(mode==="login"?"Sign In to Portal":"Create Account")}
        </Btn>

        <div style={{textAlign:"center",fontSize:12,color:T.textMuted}}>
          {mode==="login"?<>Don't have an account? <span onClick={()=>{setMode("signup");setError("")}} style={{color:T.gold,cursor:"pointer",fontWeight:600}}>Request access</span></>
          :<>Already have an account? <span onClick={()=>{setMode("login");setError("")}} style={{color:T.gold,cursor:"pointer",fontWeight:600}}>Sign in</span></>}
        </div>

        <div style={{display:"flex",alignItems:"center",gap:8,marginTop:32,fontSize:11,color:T.textDim}}>
          <I name="shield" size={13} color={T.textDim}/>
          <span>Secured with end-to-end encryption · MFA available in Settings</span>
        </div>
      </div>
    </div>
  );
}

// ═══════════════════════════════════════════════════
// DASHBOARD
// ═══════════════════════════════════════════════════
function Dashboard({user,projects,messages,invoices,T,onNavigate}){
  const activeProjects=projects.filter(p=>p.status!=="Completed");
  const pendingApprovals=projects.flatMap(p=>p.pendingApprovals||[]).filter(a=>a.status==="pending");
  const unreadMessages=messages.filter(m=>m.isEspion).length;
  const outstandingInvoices=invoices.filter(i=>i.status==="Outstanding");

  return(
    <div>
      {/* Welcome header */}
      <div style={{marginBottom:36}}>
        <h1 style={{fontFamily:font.display,fontSize:36,fontWeight:500,color:T.cream,fontStyle:"italic"}}>{["Good morning","Good morning","Good morning","Good morning","Good morning","Good morning","Good morning","Good morning","Good morning","Good morning","Good morning","Good morning","Good afternoon","Good afternoon","Good afternoon","Good afternoon","Good afternoon","Good evening","Good evening","Good evening","Good evening","Good evening","Good evening","Good evening"][new Date().getHours()]}, {(user?.full_name||user?.name||"there").split(" ")[0]}.</h1>
        <p style={{fontSize:14,color:T.textMuted,marginTop:6}}>{new Date().toLocaleDateString("en-US",{weekday:"long",year:"numeric",month:"long",day:"numeric"})}</p>
      </div>

      {/* Stat cards */}
      <div style={{display:"grid",gridTemplateColumns:"repeat(4,1fr)",gap:16,marginBottom:36}}>
        {[
          {label:"Active Projects",value:activeProjects.length,icon:"folder",color:T.gold,action:()=>onNavigate("projects")},
          {label:"Pending Approvals",value:pendingApprovals.length,icon:"alert",color:T.danger,action:()=>onNavigate("projects")},
          {label:"New Messages",value:unreadMessages,icon:"message",color:T.blue,action:()=>onNavigate("messages")},
          {label:"Outstanding Invoices",value:outstandingInvoices.length,icon:"dollar",color:pendingApprovals.length>0?T.danger:T.success,action:()=>onNavigate("invoices")},
        ].map((s,i)=>(
          <Card key={i} onClick={s.action} T={T} style={{padding:"20px 22px",borderTop:`2px solid ${s.color}30`,cursor:"pointer"}}>
            <div style={{display:"flex",justifyContent:"space-between",alignItems:"start"}}>
              <div>
                <div style={{fontFamily:font.display,fontSize:36,fontWeight:600,color:T.cream,fontStyle:"italic",lineHeight:1}}>{s.value}</div>
                <div style={{fontSize:10,color:T.textMuted,letterSpacing:".08em",textTransform:"uppercase",marginTop:6}}>{s.label}</div>
              </div>
              <I name={s.icon} size={20} color={s.color}/>
            </div>
          </Card>
        ))}
      </div>

      {/* Pending approvals alert */}
      {pendingApprovals.length>0&&(
        <div style={{background:`${T.danger}08`,border:`1px solid ${T.danger}25`,borderRadius:4,padding:"16px 20px",marginBottom:28,display:"flex",alignItems:"center",gap:12}}>
          <I name="alert" size={18} color={T.danger}/>
          <div style={{flex:1}}>
            <span style={{fontSize:13,fontWeight:600,color:T.cream}}>Action Required: </span>
            <span style={{fontSize:13,color:T.textMuted}}>{pendingApprovals.length} approval{pendingApprovals.length>1?"s":""} waiting for your response.</span>
          </div>
          <Btn small primary onClick={()=>onNavigate("projects")} T={T}>Review Now</Btn>
        </div>
      )}

      <div style={{display:"grid",gridTemplateColumns:"1fr 1fr",gap:24}}>
        {/* Active projects */}
        <Card T={T} style={{padding:0,overflow:"hidden"}}>
          <div style={{padding:"20px 24px",borderBottom:`1px solid ${T.border}`,display:"flex",justifyContent:"space-between",alignItems:"center"}}>
            <h3 style={{fontFamily:font.display,fontSize:18,fontWeight:600,color:T.cream,fontStyle:"italic"}}>Active Projects</h3>
            <span onClick={()=>onNavigate("projects")} style={{fontSize:11,color:T.gold,cursor:"pointer",fontWeight:600,letterSpacing:".04em"}}>View all →</span>
          </div>
          <div>
            {activeProjects.map((p,i)=>(
              <div key={p.id} onClick={()=>onNavigate("projects",p.id)} style={{padding:"16px 24px",borderBottom:i<activeProjects.length-1?`1px solid ${T.border}`:"none",cursor:"pointer",transition:"background .2s"}} onMouseEnter={e=>e.currentTarget.style.background=T.bg3} onMouseLeave={e=>e.currentTarget.style.background="transparent"}>
                <div style={{display:"flex",justifyContent:"space-between",alignItems:"start",marginBottom:8}}>
                  <div>
                    <div style={{fontSize:13,fontWeight:600,color:T.cream,marginBottom:3}}>{p.thumbnail} {p.name}</div>
                    <StatusBadge status={p.status} T={T}/>
                  </div>
                  <span style={{fontSize:11,color:T.textDim,fontFamily:font.mono}}>{p.progress}%</span>
                </div>
                <ProgressBar value={p.progress} T={T}/>
                <div style={{fontSize:11,color:T.textDim,marginTop:6}}>Updated {p.lastUpdate}</div>
              </div>
            ))}
          </div>
        </Card>

        {/* Recent messages */}
        <Card T={T} style={{padding:0,overflow:"hidden"}}>
          <div style={{padding:"20px 24px",borderBottom:`1px solid ${T.border}`,display:"flex",justifyContent:"space-between",alignItems:"center"}}>
            <h3 style={{fontFamily:font.display,fontSize:18,fontWeight:600,color:T.cream,fontStyle:"italic"}}>Recent Messages</h3>
            <span onClick={()=>onNavigate("messages")} style={{fontSize:11,color:T.gold,cursor:"pointer",fontWeight:600,letterSpacing:".04em"}}>View all →</span>
          </div>
          <div>
            {messages.slice(0,4).map((m,i)=>(
              <div key={m.id} onClick={()=>onNavigate("messages",m.projectId)} style={{padding:"14px 24px",borderBottom:i<3?`1px solid ${T.border}`:"none",cursor:"pointer",transition:"background .2s"}} onMouseEnter={e=>e.currentTarget.style.background=T.bg3} onMouseLeave={e=>e.currentTarget.style.background="transparent"}>
                <div style={{display:"flex",gap:10,alignItems:"start"}}>
                  <Avatar initials={m.senderInitial} size={32} T={T}/>
                  <div style={{flex:1,minWidth:0}}>
                    <div style={{display:"flex",justifyContent:"space-between",marginBottom:3}}>
                      <span style={{fontSize:12,fontWeight:600,color:T.cream}}>{m.sender}</span>
                      <span style={{fontSize:10,color:T.textDim}}>{m.time}</span>
                    </div>
                    <div style={{fontSize:12,color:T.textMuted,overflow:"hidden",textOverflow:"ellipsis",whiteSpace:"nowrap"}}>{m.text}</div>
                  </div>
                </div>
              </div>
            ))}
          </div>
        </Card>
      </div>

      {/* Outstanding invoices */}
      {outstandingInvoices.length>0&&(
        <Card T={T} style={{marginTop:24,padding:0,overflow:"hidden"}}>
          <div style={{padding:"20px 24px",borderBottom:`1px solid ${T.border}`,display:"flex",justifyContent:"space-between",alignItems:"center"}}>
            <h3 style={{fontFamily:font.display,fontSize:18,fontWeight:600,color:T.cream,fontStyle:"italic"}}>Outstanding Invoices</h3>
            <span onClick={()=>onNavigate("invoices")} style={{fontSize:11,color:T.gold,cursor:"pointer",fontWeight:600}}>View all →</span>
          </div>
          {outstandingInvoices.map((inv,i)=>(
            <div key={inv.id} style={{padding:"16px 24px",borderBottom:i<outstandingInvoices.length-1?`1px solid ${T.border}`:"none",display:"flex",justifyContent:"space-between",alignItems:"center"}}>
              <div>
                <div style={{fontSize:13,fontWeight:600,color:T.cream}}>{inv.project}</div>
                <div style={{fontSize:11,color:T.textMuted,marginTop:2}}>{inv.type} · Due {inv.dueDate}</div>
              </div>
              <div style={{display:"flex",alignItems:"center",gap:16}}>
                <span style={{fontFamily:font.display,fontSize:20,fontWeight:600,color:T.gold,fontStyle:"italic"}}>{inv.amount}</span>
                <Btn small primary onClick={()=>onNavigate("invoices")} T={T}>Pay Now</Btn>
              </div>
            </div>
          ))}
        </Card>
      )}
    </div>
  );
}

// ── Real File Upload Component ──
function RealFileUpload({projectId,T,onUploaded}){
  const[uploading,setUploading]=useState(false);
  const[error,setError]=useState("");
  const[success,setSuccess]=useState("");
  const fileRef=useRef(null);
  const handleUpload=async(e)=>{
    const file=e.target.files?.[0];
    if(!file)return;
    setUploading(true);setError("");setSuccess("");
    try{
      const path=`projects/${projectId}/${Date.now()}_${file.name}`;
      const{error:uploadError}=await supabase.storage.from("project-files").upload(path,file);
      if(uploadError)throw uploadError;
      const size=file.size<1024*1024?`${(file.size/1024).toFixed(1)} KB`:`${(file.size/1024/1024).toFixed(1)} MB`;
      const{error:dbError}=await supabase.from("project_files").insert({project_id:projectId,name:file.name,size,file_type:file.name.split(".").pop(),storage_path:path});
      if(dbError)throw dbError;
      setSuccess(`${file.name} uploaded successfully!`);
      setTimeout(()=>onUploaded(),1500);
    }catch(err){setError(err.message||"Upload failed.");}
    finally{setUploading(false);}
  };
  return(
    <div>
      <div onClick={()=>!uploading&&fileRef.current?.click()} style={{border:`2px dashed ${uploading?T.gold:T.border}`,borderRadius:4,padding:40,textAlign:"center",cursor:uploading?"default":"pointer",background:T.bg3,transition:"border-color .2s"}} onMouseEnter={e=>{if(!uploading)e.currentTarget.style.borderColor=T.gold}} onMouseLeave={e=>{if(!uploading)e.currentTarget.style.borderColor=T.border}}>
        {uploading
          ?<div style={{fontSize:14,color:T.gold}}>Uploading...</div>
          :<><I name="upload" size={32} color={T.textDim}/><div style={{fontSize:14,color:T.textMuted,marginTop:12}}>Click to select a file</div><div style={{fontSize:11,color:T.textDim,marginTop:6}}>PDF, Images, DWG, ZIP up to 100MB</div></>
        }
        <input ref={fileRef} type="file" onChange={handleUpload} style={{display:"none"}} accept=".pdf,.jpg,.jpeg,.png,.webp,.zip,.dwg,.doc,.docx"/>
      </div>
      {error&&<div style={{marginTop:12,fontSize:12,color:T.danger,padding:"10px 14px",background:`${T.danger}10`,borderRadius:3}}>{error}</div>}
      {success&&<div style={{marginTop:12,fontSize:12,color:T.success,padding:"10px 14px",background:`${T.success}10`,borderRadius:3}}>{success}</div>}
    </div>
  );
}


// ── Approval Card — selectable options ──
function ApprovalCard({apr,T,onSubmit}){
  const[selected,setSelected]=useState(null);
  const[comment,setComment]=useState("");
  const[submitting,setSubmitting]=useState(false);
  const[done,setDone]=useState(false);

  if(done) return(
    <div style={{padding:16,background:`${T.success}08`,border:`1px solid ${T.success}20`,borderRadius:3,marginBottom:8}}>
      <div style={{display:"flex",alignItems:"center",gap:8}}>
        <I name="check" size={14} color={T.success}/>
        <span style={{fontSize:13,fontWeight:600,color:T.success}}>Response submitted — Espion has been notified.</span>
      </div>
    </div>
  );

  return(
    <div style={{padding:16,background:T.bg3,borderRadius:3,marginBottom:8}}>
      <div style={{fontSize:13,fontWeight:600,color:T.cream,marginBottom:4}}>{apr.type}</div>
      <div style={{fontSize:12,color:T.textMuted,marginBottom:14,lineHeight:1.6}}>{apr.description}</div>

      {apr.options&&apr.options.length>0&&(
        <div style={{marginBottom:14}}>
          <div style={{fontSize:10,color:T.textDim,letterSpacing:".08em",textTransform:"uppercase",marginBottom:8}}>Select an option:</div>
          <div style={{display:"flex",gap:8,flexWrap:"wrap"}}>
            {apr.options.map(opt=>(
              <div key={opt} onClick={()=>setSelected(selected===opt?null:opt)} style={{padding:"8px 14px",borderRadius:3,cursor:"pointer",fontSize:12,fontWeight:selected===opt?600:400,color:selected===opt?T.bg:T.text,background:selected===opt?`linear-gradient(135deg,${T.gold},${T.goldDark})`:`${T.blue}15`,border:`1px solid ${selected===opt?T.gold:T.blue}40`,transition:"all .2s"}}>
                {selected===opt&&<I name="check" size={12} color={T.bg} style={{marginRight:4,display:"inline"}}/>}
                {opt}
              </div>
            ))}
          </div>
        </div>
      )}

      <div style={{marginBottom:12}}>
        <textarea value={comment} onChange={e=>setComment(e.target.value)} placeholder="Add a comment (optional)..." rows={2} style={{width:"100%",padding:"8px 12px",background:T.bg2,border:`1px solid ${T.border}`,borderRadius:3,color:T.text,fontSize:12,fontFamily:font.body,resize:"none",outline:"none"}}/>
      </div>

      <div style={{display:"flex",gap:8}}>
        <Btn small primary icon="check" disabled={submitting||(apr.options?.length>0&&!selected)} onClick={async()=>{
          setSubmitting(true);
          await onSubmit("approve",comment,selected);
          setDone(true);
        }} T={T}>{submitting?"Submitting...":"Approve"}</Btn>
        <Btn small danger icon="x" disabled={submitting} onClick={async()=>{
          setSubmitting(true);
          await onSubmit("decline",comment,selected);
          setDone(true);
        }} T={T}>Decline</Btn>
      </div>
      {apr.options?.length>0&&!selected&&<div style={{fontSize:11,color:T.textDim,marginTop:8}}>Please select an option above before approving.</div>}
    </div>
  );
}

// ═══════════════════════════════════════════════════
// PROJECTS
// ═══════════════════════════════════════════════════
function Projects({projects,T,focusId,onFocusId,onSubmitApproval,user}){
  const[sel,setSel]=useState(focusId?projects.find(p=>p.id===focusId):null);
  const[showApproval,setShowApproval]=useState(null);
  const[approvalComment,setApprovalComment]=useState("");
  const[approvalDecision,setApprovalDecision]=useState(null);
  const[showUpload,setShowUpload]=useState(false);
  const[filter,setFilter]=useState("All");
  const[showNewProjectRequest,setShowNewProjectRequest]=useState(false);
  const[reqName,setReqName]=useState("");
  const[reqDesc,setReqDesc]=useState("");
  const[reqSending,setReqSending]=useState(false);
  const[reqSent,setReqSent]=useState(false);

  useEffect(()=>{if(focusId){setSel(projects.find(p=>p.id===focusId)||null);}},[focusId,projects]);

  const statuses=["All","In Production","Design Review","Quoting","Completed"];
  const filtered=filter==="All"?projects:projects.filter(p=>p.status===filter);

  const handleApproval=(apr,decision)=>{
    setApprovalDecision(decision);
    setShowApproval(apr);
  };

  const submitApproval=()=>{
    alert(`${approvalDecision==="approve"?"Approved":"Declined"}: ${showApproval.type}${approvalComment?` — "${approvalComment}"`:""}\n\nEspion has been notified.`);
    setShowApproval(null);setApprovalComment("");setApprovalDecision(null);
  };

  if(sel){
    return(
      <div>
        <div style={{display:"flex",alignItems:"center",gap:12,marginBottom:28}}>
          <button onClick={()=>{setSel(null);onFocusId&&onFocusId(null)}} style={{display:"flex",alignItems:"center",gap:6,fontSize:12,color:T.textMuted,background:"none",border:"none",cursor:"pointer",fontFamily:font.body,padding:0}}>
            <I name="arrowL" size={14} color={T.textMuted}/>Back to projects
          </button>
          <span style={{color:T.border}}>/</span>
          <span style={{fontSize:12,color:T.gold,fontWeight:600}}>{sel.name}</span>
        </div>

        <div style={{display:"grid",gridTemplateColumns:"1fr 340px",gap:24}}>
          {/* Main content */}
          <div>
            {/* Project header */}
            <Card T={T} style={{marginBottom:20}}>
              <div style={{display:"flex",justifyContent:"space-between",alignItems:"start",marginBottom:16}}>
                <div>
                  <div style={{fontSize:24,marginBottom:8}}>{sel.thumbnail}</div>
                  <h2 style={{fontFamily:font.display,fontSize:26,fontWeight:600,color:T.cream,fontStyle:"italic",marginBottom:8}}>{sel.name}</h2>
                  <div style={{display:"flex",gap:8,flexWrap:"wrap"}}>
                    <StatusBadge status={sel.status} T={T}/>
                    <Badge color={sel.priority==="Urgent"?T.danger:sel.priority==="High"?T.gold:T.textMuted} T={T}>{sel.priority} Priority</Badge>
                  </div>
                </div>
                <div style={{textAlign:"right"}}>
                  <div style={{fontFamily:font.display,fontSize:28,fontWeight:600,color:T.gold,fontStyle:"italic"}}>{sel.budget}</div>
                  <div style={{fontSize:11,color:T.textDim,marginTop:2}}>Project budget</div>
                  {sel.dueDate!=="TBD"&&<div style={{fontSize:11,color:T.textMuted,marginTop:4}}>Due {sel.dueDate}</div>}
                </div>
              </div>
              <div style={{marginBottom:8}}>
                <div style={{display:"flex",justifyContent:"space-between",marginBottom:6}}>
                  <span style={{fontSize:11,color:T.textMuted,letterSpacing:".06em",textTransform:"uppercase"}}>Overall Progress</span>
                  <span style={{fontSize:11,color:T.gold,fontFamily:font.mono}}>{sel.progress}%</span>
                </div>
                <ProgressBar value={sel.progress} T={T}/>
              </div>
            </Card>

            {/* Pending approvals */}
            {sel.pendingApprovals.length>0&&(
              <Card T={T} style={{marginBottom:20,border:`1px solid ${T.danger}30`,background:`${T.danger}04`}}>
                <div style={{display:"flex",alignItems:"center",gap:8,marginBottom:16}}>
                  <I name="alert" size={16} color={T.danger}/>
                  <h3 style={{fontFamily:font.display,fontSize:17,fontWeight:600,color:T.cream,fontStyle:"italic"}}>Action Required</h3>
                </div>
                {sel.pendingApprovals.map(apr=>(
                  <ApprovalCard key={apr.id} apr={apr} T={T} onSubmit={async(decision,comment,selectedOption)=>{
                    if(onSubmitApproval){await onSubmitApproval(apr.id,decision,comment,selectedOption);}
                    // refresh
                  }}/>
                ))}
              </Card>
            )}

            {/* Pieces */}
            <Card T={T} style={{marginBottom:20}}>
              <h3 style={{fontFamily:font.display,fontSize:17,fontWeight:600,color:T.cream,fontStyle:"italic",marginBottom:14}}>Pieces in This Project</h3>
              {sel.pieces.map((piece,i)=>(
                <div key={i} style={{display:"flex",alignItems:"center",gap:10,padding:"10px 0",borderBottom:i<sel.pieces.length-1?`1px solid ${T.border}`:"none"}}>
                  <div style={{width:6,height:6,borderRadius:"50%",background:T.gold,flexShrink:0}}/>
                  <span style={{fontSize:13,color:T.text}}>{piece}</span>
                </div>
              ))}
            </Card>

            {/* Updates timeline */}
            <Card T={T}>
              <h3 style={{fontFamily:font.display,fontSize:17,fontWeight:600,color:T.cream,fontStyle:"italic",marginBottom:18}}>Project Updates</h3>
              {sel.updates.map((u,i)=>(
                <div key={i} style={{display:"flex",gap:14,marginBottom:18,position:"relative"}}>
                  <div style={{display:"flex",flexDirection:"column",alignItems:"center",flexShrink:0}}>
                    <div style={{width:10,height:10,borderRadius:"50%",background:T.gold,flexShrink:0,marginTop:3}}/>
                    {i<sel.updates.length-1&&<div style={{width:1,flex:1,background:T.border,marginTop:4}}/>}
                  </div>
                  <div style={{flex:1,paddingBottom:i<sel.updates.length-1?12:0}}>
                    <div style={{display:"flex",justifyContent:"space-between",marginBottom:4}}>
                      <span style={{fontSize:11,fontWeight:600,color:T.gold,letterSpacing:".06em",textTransform:"uppercase"}}>{u.author}</span>
                      <span style={{fontSize:10,color:T.textDim,fontFamily:font.mono}}>{u.date}</span>
                    </div>
                    <p style={{fontSize:13,color:T.text,lineHeight:1.65}}>{u.text}</p>
                  </div>
                </div>
              ))}
            </Card>
          </div>

          {/* Sidebar */}
          <div>
            {/* Files */}
            <Card T={T} style={{marginBottom:16,padding:0,overflow:"hidden"}}>
              <div style={{padding:"16px 20px",borderBottom:`1px solid ${T.border}`,display:"flex",justifyContent:"space-between",alignItems:"center"}}>
                <h4 style={{fontSize:13,fontWeight:600,color:T.cream}}>Project Files</h4>
                <Btn small icon="upload" onClick={()=>setShowUpload(true)} T={T}>Upload</Btn>
              </div>
              {sel.files.map((f,i)=>(
                <div key={i} style={{padding:"12px 20px",borderBottom:i<sel.files.length-1?`1px solid ${T.border}`:"none",display:"flex",alignItems:"center",gap:10}}>
                  <I name="file" size={14} color={T.gold}/>
                  <div style={{flex:1,minWidth:0}}>
                    <div style={{fontSize:12,color:T.text,overflow:"hidden",textOverflow:"ellipsis",whiteSpace:"nowrap"}}>{f.name}</div>
                    <div style={{fontSize:10,color:T.textDim}}>{f.size} · {f.date}</div>
                  </div>
                  <div style={{display:"flex",gap:6,flexShrink:0}}>
                    <span title="View" onClick={async()=>{const p=f.storagePath||f.storage_path;if(p){const{data}=await supabase.storage.from("project-files").createSignedUrl(p,60);if(data?.signedUrl)window.open(data.signedUrl,"_blank");}}} style={{cursor:"pointer"}}><I name="eye" size={13} color={T.textDim}/></span>
                    <span title="Download" onClick={async()=>{const p=f.storagePath||f.storage_path;if(p){const{data}=await supabase.storage.from("project-files").createSignedUrl(p,60);if(data?.signedUrl){const a=document.createElement("a");a.href=data.signedUrl;a.download=f.name;a.click();}}}} style={{cursor:"pointer"}}><I name="download" size={13} color={T.textDim}/></span>
                  </div>
                </div>
              ))}
            </Card>

            {/* Quick info */}
            <Card T={T}>
              <h4 style={{fontSize:13,fontWeight:600,color:T.cream,marginBottom:14}}>Project Details</h4>
              {[{l:"Status",v:<StatusBadge status={sel.status} T={T}/>},{l:"Stage",v:sel.stage},{l:"Budget",v:sel.budget},{l:"Due Date",v:sel.dueDate},{l:"Last Update",v:sel.lastUpdate}].map(r=>(
                <div key={r.l} style={{display:"flex",justifyContent:"space-between",alignItems:"center",marginBottom:12}}>
                  <span style={{fontSize:11,color:T.textDim,textTransform:"uppercase",letterSpacing:".08em"}}>{r.l}</span>
                  <span style={{fontSize:12,color:T.text}}>{r.v}</span>
                </div>
              ))}
            </Card>
          </div>
        </div>

        {showUpload&&(
          <Modal onClose={()=>setShowUpload(false)} T={T}>
            <div style={{display:"flex",justifyContent:"space-between",alignItems:"center",marginBottom:20}}>
              <h2 style={{fontFamily:font.display,fontSize:22,fontWeight:600,color:T.cream,fontStyle:"italic"}}>Upload File</h2>
              <span onClick={()=>setShowUpload(false)} style={{cursor:"pointer",color:T.textMuted}}><I name="x" size={18}/></span>
            </div>
            <RealFileUpload projectId={sel.id} T={T} onUploaded={()=>setShowUpload(false)}/>
          </Modal>
        )}
      </div>
    );
  }

  return(
    <div>
      <div style={{display:"flex",justifyContent:"space-between",alignItems:"center",marginBottom:28}}>
        <div>
          <h2 style={{fontFamily:font.display,fontSize:32,fontWeight:600,color:T.cream,fontStyle:"italic"}}>Your Projects</h2>
          <p style={{fontSize:13,color:T.textMuted,marginTop:4}}>{projects.length} projects · {projects.filter(p=>p.status!=="Completed").length} active</p>
        </div>
        <Btn primary icon="plus" onClick={()=>setShowNewProjectRequest(true)} T={T}>Request Project</Btn>
      </div>

      <div style={{display:"flex",gap:6,marginBottom:24,flexWrap:"wrap"}}>
        {statuses.map(s=><span key={s} onClick={()=>setFilter(s)} style={{padding:"6px 14px",fontSize:11,fontWeight:500,cursor:"pointer",borderRadius:2,background:filter===s?T.gold:"transparent",color:filter===s?T.bg:T.textMuted,border:`1px solid ${filter===s?T.gold:T.border}`,transition:"all .2s",fontFamily:font.body}}>{s}</span>)}
      </div>

      <div style={{display:"flex",flexDirection:"column",gap:12}}>
        {filtered.map(p=>(
          <Card key={p.id} onClick={()=>setSel(p)} T={T} style={{padding:0,overflow:"hidden"}}>
            <div style={{padding:"20px 24px"}}>
              <div style={{display:"flex",justifyContent:"space-between",alignItems:"start",marginBottom:14}}>
                <div style={{flex:1}}>
                  <div style={{display:"flex",alignItems:"center",gap:10,marginBottom:6}}>
                    <span style={{fontSize:20}}>{p.thumbnail}</span>
                    <h3 style={{fontFamily:font.display,fontSize:18,fontWeight:600,color:T.cream,fontStyle:"italic"}}>{p.name}</h3>
                  </div>
                  <div style={{display:"flex",gap:8,flexWrap:"wrap"}}>
                    <StatusBadge status={p.status} T={T}/>
                    {p.pendingApprovals.length>0&&<Badge color={T.danger} T={T}>{p.pendingApprovals.length} approval{p.pendingApprovals.length>1?"s":""} needed</Badge>}
                  </div>
                </div>
                <div style={{textAlign:"right",flexShrink:0}}>
                  <div style={{fontFamily:font.display,fontSize:22,fontWeight:600,color:T.gold,fontStyle:"italic"}}>{p.budget}</div>
                  <div style={{fontSize:11,color:T.textDim,marginTop:2}}>{p.pieces.length} pieces</div>
                </div>
              </div>
              <div style={{display:"flex",alignItems:"center",gap:12}}>
                <div style={{flex:1}}><ProgressBar value={p.progress} T={T}/></div>
                <span style={{fontSize:11,color:T.textDim,fontFamily:font.mono,flexShrink:0}}>{p.progress}%</span>
                <span style={{fontSize:11,color:T.textDim}}>Updated {p.lastUpdate}</span>
              </div>
            </div>
          </Card>
        ))}
      </div>
    </div>
  );
}

// ═══════════════════════════════════════════════════
// MESSAGES
// ═══════════════════════════════════════════════════
function Messages({projects,messages,T,focusProjectId,onSend,userId,userName,userInitial}){
  const[activeProjectId,setActiveProjectId]=useState(focusProjectId||projects[0]?.id);
  const[newMessage,setNewMessage]=useState("");
  const[allMessages,setAllMessages]=useState(messages);
  const[sending,setSending]=useState(false);
  const bottomRef=useRef(null);

  // Sync when real messages load from Supabase
  useEffect(()=>{if(messages.length>0)setAllMessages(messages);},[messages]);

  const activeProject=projects.find(p=>p.id===activeProjectId);
  // Normalize both projectId and project_id fields
  const projectMessages=allMessages.filter(m=>(m.projectId||m.project_id)===activeProjectId);

  useEffect(()=>{if(projectMessages.length>0)bottomRef.current?.scrollIntoView({behavior:"smooth"});},[activeProjectId,projectMessages.length]);
  useEffect(()=>{if(focusProjectId)setActiveProjectId(focusProjectId);},[focusProjectId]);

  const sendMessage=async()=>{
    if(!newMessage.trim()||sending)return;
    const text=newMessage;
    setNewMessage("");setSending(true);
    // Optimistic update
    const tempMsg={id:`temp_${Date.now()}`,projectId:activeProjectId,project_id:activeProjectId,sender_name:userName||"Designer",senderInitial:userInitial||"D",sender_initial:userInitial||"D",is_espion:false,isEspion:false,text,time:new Date().toLocaleTimeString("en-US",{hour:"2-digit",minute:"2-digit"}),date:"Today",created_at:new Date().toISOString()};
    setAllMessages(prev=>[...prev,tempMsg]);
    if(onSend){
      await onSend({projectId:activeProjectId,senderName:userName||"Designer",senderInitial:userInitial||"D",isEspion:false,text,senderId:userId});
      // Notify admin
      try{
        const proj=projects.find(p=>p.id===activeProjectId);
        fetch(`${process.env.REACT_APP_SUPABASE_URL}/functions/v1/send-email`,{
          method:"POST",headers:{"Content-Type":"application/json"},
          body:JSON.stringify({type:"admin_notify",to:"adam@espionfurniture.com,ryanwalters@mac.com",data:{
            event:"New message from designer",
            designerName:userName||"Designer",
            projectName:proj?.name||"Unknown project",
            messageText:text,
            link:"https://admin.espionfurniture.com"
          }})
        }).catch(()=>{});
      }catch(e){}
    }
    setSending(false);
  };

  return(
    <div style={{display:"grid",gridTemplateColumns:"280px 1fr",gap:0,background:T.bg2,border:`1px solid ${T.border}`,borderRadius:4,height:580}}>
      {/* Project list — fixed, independent scroll */}
      <div style={{borderRight:`1px solid ${T.border}`,display:"flex",flexDirection:"column",height:"100%",overflow:"hidden"}}>
        <div style={{padding:"16px 20px",borderBottom:`1px solid ${T.border}`,flexShrink:0}}>
          <h3 style={{fontFamily:font.display,fontSize:16,fontWeight:600,color:T.cream,fontStyle:"italic"}}>Conversations</h3>
        </div>
        <div style={{flex:1,overflowY:"auto",overflowX:"hidden"}}>
          {projects.map(p=>{
            const lastMsg=allMessages.filter(m=>(m.projectId||m.project_id)===p.id).slice(-1)[0];
            const isActive=activeProjectId===p.id;
            return(
              <div key={p.id} onClick={()=>setActiveProjectId(p.id)} style={{padding:"14px 20px",cursor:"pointer",background:isActive?`${T.gold}10`:"transparent",borderRight:isActive?`2px solid ${T.gold}`:"2px solid transparent",transition:"all .2s"}} onMouseEnter={e=>{if(!isActive)e.currentTarget.style.background=T.bg3}} onMouseLeave={e=>{if(!isActive)e.currentTarget.style.background="transparent"}}>
                <div style={{display:"flex",alignItems:"center",gap:8,marginBottom:4}}>
                  <span style={{fontSize:16}}>{p.thumbnail}</span>
                  <span style={{fontSize:12,fontWeight:600,color:isActive?T.gold:T.cream,overflow:"hidden",textOverflow:"ellipsis",whiteSpace:"nowrap"}}>{(p.name||"").split("—")[0].trim()}</span>
                </div>
                {lastMsg&&<div style={{fontSize:11,color:T.textMuted,overflow:"hidden",textOverflow:"ellipsis",whiteSpace:"nowrap",paddingLeft:24}}>{lastMsg.text}</div>}
              </div>
            );
          })}
        </div>
      </div>

      {/* Chat area — independent scroll */}
      <div style={{display:"flex",flexDirection:"column",height:"100%",overflow:"hidden"}}>
        <div style={{padding:"16px 24px",borderBottom:`1px solid ${T.border}`,display:"flex",alignItems:"center",gap:12}}>
          <span style={{fontSize:20}}>{activeProject?.thumbnail}</span>
          <div>
            <div style={{fontSize:14,fontWeight:600,color:T.cream}}>{activeProject?.name}</div>
            <div style={{fontSize:11,color:T.textMuted}}>Espion Team · Typically responds within 2 hours</div>
          </div>
        </div>

        <div style={{flex:1,overflow:"auto",padding:"20px 24px"}}>
          {projectMessages.length===0&&(
            <div style={{textAlign:"center",padding:40,color:T.textMuted}}>
              <I name="message" size={32} color={T.textDim}/>
              <div style={{marginTop:12,fontSize:13}}>No messages yet. Start the conversation!</div>
            </div>
          )}
          {projectMessages.map((m,i)=>{
            const isMe=!(m.isEspion||m.is_espion);
            const initials=(m.senderInitial||m.sender_initial||"?");
            const senderName=(m.sender||m.sender_name||"Espion Team");
            const msgTime=m.time||(m.created_at?new Date(m.created_at).toLocaleTimeString("en-US",{hour:"2-digit",minute:"2-digit"}):"");
            return(
              <div key={m.id} style={{display:"flex",gap:10,marginBottom:16,justifyContent:isMe?"flex-end":"flex-start"}}>
                {!isMe&&<Avatar initials={initials} size={32} T={T}/>}
                <div style={{maxWidth:"72%"}}>
                  {!isMe&&<div style={{fontSize:11,color:T.textMuted,marginBottom:4}}>{senderName} · {msgTime}</div>}
                  <div style={{padding:"10px 14px",borderRadius:isMe?"12px 12px 2px 12px":"12px 12px 12px 2px",background:isMe?`linear-gradient(135deg,${T.gold},${T.goldDark})`:T.bg3,color:isMe?T.bg:T.text,fontSize:13,lineHeight:1.6}}>
                    {m.text}
                  </div>
                  {isMe&&<div style={{fontSize:11,color:T.textMuted,marginTop:4,textAlign:"right"}}>{msgTime}</div>}
                </div>
                {isMe&&<Avatar initials={userInitial||"D"} size={32} T={T}/>}
              </div>
            );
          })}
          <div ref={bottomRef}/>
        </div>

        <div style={{padding:"16px 24px",borderTop:`1px solid ${T.border}`,display:"flex",gap:10}}>
          <input value={newMessage} onChange={e=>setNewMessage(e.target.value)} onKeyDown={e=>e.key==="Enter"&&!e.shiftKey&&sendMessage()} placeholder="Type a message..." style={{flex:1,padding:"10px 14px",background:T.inputBg,border:`1px solid ${T.border}`,borderRadius:3,color:T.text,fontSize:13,fontFamily:font.body,outline:"none"}}/>
          <Btn primary small icon="send" onClick={sendMessage} T={T} disabled={!newMessage.trim()||sending}>{sending?"...":"Send"}</Btn>
        </div>
      </div>
    </div>
  );
}

// ═══════════════════════════════════════════════════
// INVOICES
// ═══════════════════════════════════════════════════
function Invoices({invoices,T}){
  const total=invoices.filter(i=>i.status==="Outstanding").reduce((acc)=>acc+1,0);
  const paid=invoices.filter(i=>i.status==="Paid").length;

  return(
    <div>
      <div style={{marginBottom:28}}>
        <h2 style={{fontFamily:font.display,fontSize:32,fontWeight:600,color:T.cream,fontStyle:"italic"}}>Invoices & Payments</h2>
        <p style={{fontSize:13,color:T.textMuted,marginTop:4}}>All payments processed securely through Square</p>
      </div>

      <div style={{display:"grid",gridTemplateColumns:"repeat(3,1fr)",gap:16,marginBottom:32}}>
        {[
          {label:"Outstanding",value:invoices.filter(i=>i.status==="Outstanding").length,color:T.danger,sub:`${invoices.filter(i=>i.status==="Outstanding").map(i=>i.amount).join(", ")||"None"}`},
          {label:"Paid This Year",value:paid,color:T.success,sub:"All payments confirmed"},
          {label:"Upcoming",value:invoices.filter(i=>i.status==="Upcoming").length,color:T.blue,sub:"Scheduled payments"},
        ].map((s,i)=>(
          <Card key={i} T={T} style={{borderTop:`2px solid ${s.color}30`}}>
            <div style={{fontFamily:font.display,fontSize:36,fontWeight:600,color:T.cream,fontStyle:"italic"}}>{s.value}</div>
            <div style={{fontSize:10,color:T.textMuted,letterSpacing:".08em",textTransform:"uppercase",margin:"6px 0 4px"}}>{s.label}</div>
            <div style={{fontSize:11,color:T.textDim}}>{s.sub}</div>
          </Card>
        ))}
      </div>

      <Card T={T} style={{padding:0,overflow:"hidden"}}>
        <div style={{padding:"16px 24px",borderBottom:`1px solid ${T.border}`}}>
          <h3 style={{fontFamily:font.display,fontSize:18,fontWeight:600,color:T.cream,fontStyle:"italic"}}>Invoice History</h3>
        </div>
        <div style={{padding:"0 24px 0 24px"}}>
          <div style={{display:"grid",gridTemplateColumns:"1fr 2fr 1fr 1fr 140px",padding:"10px 0",borderBottom:`1px solid ${T.border}`,fontSize:9,fontWeight:700,letterSpacing:".12em",textTransform:"uppercase",color:T.textDim}}>
            <span>Invoice</span><span>Project</span><span>Amount</span><span>Status</span><span></span>
          </div>
          {invoices.map((inv,i)=>{
            const sc={Paid:T.success,Outstanding:T.danger,Upcoming:T.blue};
            return(
              <div key={inv.id} style={{display:"grid",gridTemplateColumns:"1fr 2fr 1fr 1fr 140px",padding:"16px 0",borderBottom:i<invoices.length-1?`1px solid ${T.border}`:"none",alignItems:"center"}}>
                <div>
                  <div style={{fontSize:12,fontWeight:600,color:T.cream}}>{inv.id}</div>
                  <div style={{fontSize:10,color:T.textDim,marginTop:2}}>{inv.date}</div>
                </div>
                <div>
                  <div style={{fontSize:12,color:T.text}}>{inv.project}</div>
                  <div style={{fontSize:10,color:T.textDim,marginTop:2}}>{inv.type}</div>
                </div>
                <div style={{fontFamily:font.display,fontSize:16,fontWeight:600,color:T.gold,fontStyle:"italic"}}>{inv.amount}</div>
                <Badge color={sc[inv.status]} T={T}>{inv.status}</Badge>
                <div style={{display:"flex",gap:8}}>
                  <Btn small icon="download" onClick={()=>alert("Invoice PDF download coming soon. Contact Espion for a copy.")} T={T}>PDF</Btn>
                  {inv.status==="Outstanding"&&<Btn small primary onClick={()=>{if(inv.squareUrl&&inv.squareUrl!=="#")window.open(inv.squareUrl,"_blank");else alert("Payment link not yet configured. Contact Espion to pay.");}} T={T}>Pay Now</Btn>}
                </div>
              </div>
            );
          })}
        </div>
      </Card>

      <div style={{marginTop:20,padding:"16px 20px",background:T.bg3,borderRadius:4,border:`1px solid ${T.border}`,display:"flex",alignItems:"center",gap:12}}>
        <I name="shield" size={16} color={T.textDim}/>
        <span style={{fontSize:12,color:T.textMuted}}>All payments are processed securely by Square. Espion does not store your payment information.</span>
      </div>
    </div>
  );
}

// ═══════════════════════════════════════════════════
// FILES
// ═══════════════════════════════════════════════════
function Files({projects,T}){
  const allFiles=projects.flatMap(p=>p.files.map(f=>({...f,projectName:p.name,projectId:p.id})));
  const[filter,setFilter]=useState("All");
  const types=["All","pdf","image","archive"];
  const filtered=filter==="All"?allFiles:allFiles.filter(f=>f.type===filter);

  return(
    <div>
      <div style={{display:"flex",justifyContent:"space-between",alignItems:"center",marginBottom:28}}>
        <div>
          <h2 style={{fontFamily:font.display,fontSize:32,fontWeight:600,color:T.cream,fontStyle:"italic"}}>Files & Documents</h2>
          <p style={{fontSize:13,color:T.textMuted,marginTop:4}}>{allFiles.length} files across all projects</p>
        </div>
        <Btn primary icon="upload" onClick={()=>alert("To upload files, open a specific project and use the Upload button there.")} T={T}>Upload File</Btn>
      </div>

      <div style={{display:"flex",gap:6,marginBottom:24}}>
        {types.map(t=><span key={t} onClick={()=>setFilter(t)} style={{padding:"6px 14px",fontSize:11,fontWeight:500,cursor:"pointer",borderRadius:2,background:filter===t?T.gold:"transparent",color:filter===t?T.bg:T.textMuted,border:`1px solid ${filter===t?T.gold:T.border}`,transition:"all .2s",fontFamily:font.body,textTransform:"capitalize"}}>{t}</span>)}
      </div>

      <Card T={T} style={{padding:0,overflow:"hidden"}}>
        <div style={{padding:"0 24px"}}>
          <div style={{display:"grid",gridTemplateColumns:"2fr 2fr 1fr 1fr 120px",padding:"10px 0",borderBottom:`1px solid ${T.border}`,fontSize:9,fontWeight:700,letterSpacing:".12em",textTransform:"uppercase",color:T.textDim}}>
            <span>File Name</span><span>Project</span><span>Size</span><span>Date</span><span></span>
          </div>
          {filtered.map((f,i)=>(
            <div key={i} style={{display:"grid",gridTemplateColumns:"2fr 2fr 1fr 1fr 100px",padding:"14px 0",borderBottom:i<filtered.length-1?`1px solid ${T.border}`:"none",alignItems:"center"}}>
              <div style={{display:"flex",alignItems:"center",gap:10}}>
                <I name="file" size={16} color={T.gold}/>
                <span style={{fontSize:12,color:T.text}}>{f.name}</span>
              </div>
              <span style={{fontSize:11,color:T.textMuted,overflow:"hidden",textOverflow:"ellipsis",whiteSpace:"nowrap"}}>{f.projectName}</span>
              <span style={{fontSize:11,color:T.textDim}}>{f.size}</span>
              <span style={{fontSize:11,color:T.textDim}}>{f.date}</span>
              <div style={{display:"flex",gap:6}}>
                <Btn small icon="eye" onClick={async()=>{
                  if(f.storagePath||f.storage_path){
                    const path=f.storagePath||f.storage_path;
                    const{data}=await supabase.storage.from("project-files").createSignedUrl(path,60);
                    if(data?.signedUrl)window.open(data.signedUrl,"_blank");
                  }
                }} T={T}>View</Btn>
                <Btn small icon="download" onClick={async()=>{
                  if(f.storagePath||f.storage_path){
                    const path=f.storagePath||f.storage_path;
                    const{data}=await supabase.storage.from("project-files").createSignedUrl(path,60);
                    if(data?.signedUrl){const a=document.createElement("a");a.href=data.signedUrl;a.download=f.name;a.click();}
                  }
                }} T={T}>Get</Btn>
              </div>
            </div>
          ))}
        </div>
      </Card>
    </div>
  );
}

// ═══════════════════════════════════════════════════
// RESOURCES
// ═══════════════════════════════════════════════════
function Resources({T,resources:resourcesProp}){
  const allRes=resourcesProp||MOCK_RESOURCES;
  const cats=["All",...new Set(allRes.map(r=>r.cat))];
  const[filter,setFilter]=useState("All");
  const filtered=filter==="All"?allRes:allRes.filter(r=>r.cat===filter);

  return(
    <div>
      <div style={{marginBottom:28}}>
        <h2 style={{fontFamily:font.display,fontSize:32,fontWeight:600,color:T.cream,fontStyle:"italic"}}>Resource Library</h2>
        <p style={{fontSize:13,color:T.textMuted,marginTop:4}}>Catalogs, material guides, and designer resources from Espion</p>
      </div>

      <div style={{display:"flex",gap:6,marginBottom:24,flexWrap:"wrap"}}>
        {cats.map(c=><span key={c} onClick={()=>setFilter(c)} style={{padding:"6px 14px",fontSize:11,fontWeight:500,cursor:"pointer",borderRadius:2,background:filter===c?T.gold:"transparent",color:filter===c?T.bg:T.textMuted,border:`1px solid ${filter===c?T.gold:T.border}`,transition:"all .2s",fontFamily:font.body}}>{c}</span>)}
      </div>

      <div style={{display:"grid",gridTemplateColumns:"repeat(auto-fill,minmax(280px,1fr))",gap:16}}>
        {filtered.map(r=>(
          <Card key={r.id} T={T} style={{padding:0,overflow:"hidden"}}>
            <div style={{padding:"20px 20px 16px",background:`linear-gradient(135deg,${T.bg3},${T.bg4})`}}>
              <div style={{display:"flex",justifyContent:"space-between",alignItems:"start",marginBottom:8}}>
                <Badge color={T.blue} T={T}>{r.cat}</Badge>
                <span style={{fontSize:10,color:T.textDim,fontFamily:font.mono}}>{r.size}</span>
              </div>
              <h3 style={{fontFamily:font.display,fontSize:16,fontWeight:600,color:T.cream,fontStyle:"italic",lineHeight:1.3}}>{r.title}</h3>
            </div>
            <div style={{padding:"14px 20px"}}>
              <p style={{fontSize:12,color:T.textMuted,lineHeight:1.6,marginBottom:14}}>{r.desc}</p>
              <div style={{display:"flex",gap:8}}>
                <Btn small primary icon="download" onClick={async()=>{
                  const url=r.download_url||r.downloadUrl;
                  if(url){window.open(url,"_blank");}
                  else if(r.storage_path){
                    const{data}=await supabase.storage.from("project-files").createSignedUrl(r.storage_path,120);
                    if(data?.signedUrl){const a=document.createElement("a");a.href=data.signedUrl;a.download=r.title;a.click();}
                  }else{alert("This resource doesn\'t have a download link yet. Contact Espion for access.");}
                }} T={T}>Download</Btn>
                <Btn small icon="eye" onClick={async()=>{
                  const url=r.download_url||r.downloadUrl;
                  if(url){window.open(url,"_blank");}
                  else if(r.storage_path){
                    const{data}=await supabase.storage.from("project-files").createSignedUrl(r.storage_path,120);
                    if(data?.signedUrl)window.open(data.signedUrl,"_blank");
                  }else{alert("This resource doesn\'t have a preview link yet. Contact Espion for access.");}
                }} T={T}>Preview</Btn>
              </div>
            </div>
          </Card>
        ))}
      </div>
    </div>
  );
}

// ── MFA Enrollment Component ──
function MFAEnrollment({T,supabase}){
  const[step,setStep]=useState("idle"); // idle | enrolling | verifying | enabled | error
  const[qrCode,setQrCode]=useState("");
  const[secret,setSecret]=useState("");
  const[factorId,setFactorId]=useState("");
  const[code,setCode]=useState("");
  const[error,setError]=useState("");
  const[factors,setFactors]=useState([]);

  useEffect(()=>{
    supabase.auth.mfa.listFactors().then(({data})=>{
      setFactors(data?.totp||[]);
    });
  },[]);

  const startEnroll=async()=>{
    setError("");setStep("enrolling");
    const{data,error:e}=await supabase.auth.mfa.enroll({factorType:"totp",friendlyName:"Espion Portal"});
    if(e){setError(e.message);setStep("error");return;}
    setQrCode(data.totp.qr_code);
    setSecret(data.totp.secret);
    setFactorId(data.id);
    setStep("verifying");
  };

  const verifyEnroll=async()=>{
    setError("");
    const{data:challengeData,error:ce}=await supabase.auth.mfa.challenge({factorId});
    if(ce){setError(ce.message);return;}
    const{error:ve}=await supabase.auth.mfa.verify({factorId,challengeId:challengeData.id,code});
    if(ve){setError("Invalid code. Please try again.");return;}
    setStep("enabled");
    setFactors(prev=>[...prev,{id:factorId,status:"verified"}]);
  };

  const unenroll=async(id)=>{
    const{error:e}=await supabase.auth.mfa.unenroll({factorId:id});
    if(!e)setFactors(prev=>prev.filter(f=>f.id!==id));
  };

  const verifiedFactors=factors.filter(f=>f.status==="verified");

  if(step==="enabled"||verifiedFactors.length>0){
    return(
      <div style={{padding:"14px 16px",background:`${T.success}10`,border:`1px solid ${T.success}25`,borderRadius:3}}>
        <div style={{display:"flex",alignItems:"center",gap:8,marginBottom:8}}>
          <I name="shield" size={16} color={T.success}/>
          <span style={{fontSize:13,fontWeight:600,color:T.cream}}>Two-Factor Authentication is enabled</span>
        </div>
        <p style={{fontSize:12,color:T.textMuted,marginBottom:12}}>Your account is protected with an authenticator app.</p>
        {verifiedFactors.map(f=>(
          <div key={f.id} style={{display:"flex",justifyContent:"space-between",alignItems:"center"}}>
            <span style={{fontSize:12,color:T.textDim}}>Authenticator app · {f.friendly_name||"Espion Portal"}</span>
            <Btn small danger onClick={()=>unenroll(f.id)} T={T}>Remove</Btn>
          </div>
        ))}
      </div>
    );
  }

  if(step==="verifying"){
    return(
      <div>
        <div style={{marginBottom:16}}>
          <p style={{fontSize:12,color:T.textMuted,marginBottom:12}}>Scan this QR code with your authenticator app (Google Authenticator, Authy, etc.), then enter the 6-digit code below.</p>
          <div style={{background:"#fff",padding:12,borderRadius:4,display:"inline-block",marginBottom:16}}>
            <img src={qrCode} alt="MFA QR Code" style={{width:160,height:160,display:"block"}}/>
          </div>
          <div style={{padding:"10px 14px",background:T.bg3,borderRadius:3,marginBottom:16,fontSize:11,color:T.textDim,fontFamily:font.mono,wordBreak:"break-all"}}>
            Manual key: {secret}
          </div>
        </div>
        <div style={{marginBottom:12}}>
          <label style={{display:"block",fontSize:10,fontWeight:600,letterSpacing:".1em",textTransform:"uppercase",color:T.textMuted,marginBottom:7}}>Verification Code</label>
          <input value={code} onChange={e=>setCode(e.target.value.replace(/\D/g,"").slice(0,6))} placeholder="000000" maxLength={6} style={{width:"100%",maxWidth:200,padding:"10px 14px",background:T.inputBg,border:`1px solid ${T.border}`,borderRadius:3,color:T.text,fontSize:20,fontFamily:font.mono,letterSpacing:".2em",outline:"none",textAlign:"center"}}/>
        </div>
        {error&&<div style={{fontSize:12,color:T.danger,marginBottom:12}}>{error}</div>}
        <div style={{display:"flex",gap:8}}>
          <Btn primary icon="check" onClick={verifyEnroll} disabled={code.length!==6} T={T}>Verify & Enable</Btn>
          <Btn ghost onClick={()=>setStep("idle")} T={T}>Cancel</Btn>
        </div>
      </div>
    );
  }

  return(
    <div>
      {error&&<div style={{fontSize:12,color:T.danger,marginBottom:12,padding:"10px 14px",background:`${T.danger}10`,borderRadius:3}}>{error}</div>}
      <Btn icon="shield" onClick={startEnroll} T={T}>Set Up Authenticator App</Btn>
    </div>
  );
}

// ═══════════════════════════════════════════════════
// SETTINGS
// ═══════════════════════════════════════════════════
function Settings({user,T,theme,setTheme,onSignOut}){
  const[name,setName]=useState(user?.full_name||user?.name||"");
  const[firm,setFirm]=useState(user?.firm||"");
  const[email,setEmail]=useState(user.email||"");
  const[phone,setPhone]=useState(user?.phone||"");
  const[notifyEmail,setNotifyEmail]=useState(user?.notify_email!==undefined?user.notify_email:true);
  const[notifyApprovals,setNotifyApprovals]=useState(user?.notify_approvals!==undefined?user.notify_approvals:true);
  const[notifyMessages,setNotifyMessages]=useState(user?.notify_messages!==undefined?user.notify_messages:true);
  useEffect(()=>{
    if(user?.notify_email!==undefined)setNotifyEmail(user.notify_email);
    if(user?.notify_approvals!==undefined)setNotifyApprovals(user.notify_approvals);
    if(user?.notify_messages!==undefined)setNotifyMessages(user.notify_messages);
  },[user?.id]);
  const[saved,setSaved]=useState(false);
  const[activeTab,setActiveTab]=useState("profile");
  const[passwordMsg,setPasswordMsg]=useState("");

  const save=async()=>{
    if(!user?.id)return;
    const{error}=await supabase.from("profiles").update({
      full_name:name,firm,phone,
      notify_email:notifyEmail,notify_approvals:notifyApprovals,notify_messages:notifyMessages,
      updated_at:new Date().toISOString()
    }).eq("id",user.id);
    if(!error){setSaved(true);setTimeout(()=>setSaved(false),2500);}
  };
  const saveNotifications=async()=>{
    if(!user?.id)return;
    await supabase.from("profiles").update({notify_email:notifyEmail,notify_approvals:notifyApprovals,notify_messages:notifyMessages}).eq("id",user.id);
    setSaved(true);setTimeout(()=>setSaved(false),2500);
  };
  const changePassword=async()=>{
    const{error}=await supabase.auth.resetPasswordForEmail(email||user?.email,{redirectTo:window.location.origin});
    if(!error){setPasswordMsg("Password reset email sent — check your inbox.");}
    else{setPasswordMsg("Error sending reset email.");}
  };

  const Toggle=({value,onChange})=>(
    <div onClick={()=>onChange(!value)} style={{width:40,height:22,borderRadius:11,background:value?T.gold:T.bg4,cursor:"pointer",position:"relative",transition:"background .2s",flexShrink:0}}>
      <div style={{position:"absolute",top:3,left:value?20:3,width:16,height:16,borderRadius:"50%",background:T.bg,transition:"left .2s",boxShadow:`0 1px 3px ${T.shadow}`}}/>
    </div>
  );

  const tabs=["profile","notifications","security","preferences"];

  return(
    <div>
      <div style={{marginBottom:28}}>
        <h2 style={{fontFamily:font.display,fontSize:32,fontWeight:600,color:T.cream,fontStyle:"italic"}}>Settings</h2>
        <p style={{fontSize:13,color:T.textMuted,marginTop:4}}>Manage your account and preferences</p>
      </div>

      <div style={{display:"grid",gridTemplateColumns:"200px 1fr",gap:24}}>
        {/* Tab nav */}
        <div>
          {tabs.map(tab=>(
            <div key={tab} onClick={()=>setActiveTab(tab)} style={{padding:"10px 14px",marginBottom:4,borderRadius:3,cursor:"pointer",background:activeTab===tab?`${T.gold}12`:"transparent",borderRight:activeTab===tab?`2px solid ${T.gold}`:"2px solid transparent",transition:"all .2s",display:"flex",alignItems:"center",gap:8}} onMouseEnter={e=>{if(activeTab!==tab)e.currentTarget.style.background=T.bg3}} onMouseLeave={e=>{if(activeTab!==tab)e.currentTarget.style.background="transparent"}}>
              <I name={tab==="profile"?"users":tab==="notifications"?"bell":tab==="security"?"shield":"settings"} size={14} color={activeTab===tab?T.gold:T.textDim}/>
              <span style={{fontSize:12,fontWeight:activeTab===tab?600:400,color:activeTab===tab?T.cream:T.textMuted,textTransform:"capitalize"}}>{tab}</span>
            </div>
          ))}
          <Divider T={T}/>
          <div onClick={onSignOut} style={{padding:"10px 14px",borderRadius:3,cursor:"pointer",display:"flex",alignItems:"center",gap:8,color:T.danger}} onMouseEnter={e=>e.currentTarget.style.background=`${T.danger}10`} onMouseLeave={e=>e.currentTarget.style.background="transparent"}>
            <I name="logout" size={14} color={T.danger}/>
            <span style={{fontSize:12,fontWeight:600}}>Sign Out</span>
          </div>
        </div>

        {/* Tab content */}
        <Card T={T}>
          {activeTab==="profile"&&<>
            <h3 style={{fontFamily:font.display,fontSize:20,fontWeight:600,color:T.cream,fontStyle:"italic",marginBottom:24}}>Profile Information</h3>
            <div style={{display:"flex",alignItems:"center",gap:16,marginBottom:28}}>
              <Avatar initials={(user?.full_name||user?.name||user?.email||"?").charAt(0).toUpperCase()} size={64} T={T}/>
              <div>
                <div style={{fontSize:16,fontWeight:600,color:T.cream}}>{user?.full_name||user?.name||"—"}</div>
                <div style={{fontSize:12,color:T.textMuted,marginTop:2}}>{user?.tier||"Partner"}</div>
                <div style={{fontSize:11,color:T.textDim,marginTop:2}}>Member since {user.member_since?new Date(user.member_since).toLocaleDateString("en-US",{year:"numeric",month:"long"}):user.memberSince||"—"}</div>
              </div>
            </div>
            <div style={{display:"grid",gridTemplateColumns:"1fr 1fr",gap:16}}>
              <Field label="Full Name" value={name} onChange={setName} placeholder="Jane Smith" T={T}/>
              <Field label="Design Firm" value={firm} onChange={setFirm} placeholder="Smith Interiors" T={T}/>
            </div>
            <Field label="Email Address" value={email} onChange={setEmail} placeholder="you@firm.com" type="email" T={T}/>
            <div style={{marginBottom:16}}>
              <label style={{display:"block",fontSize:10,fontWeight:600,letterSpacing:".1em",textTransform:"uppercase",color:T.textMuted,marginBottom:7,fontFamily:font.body}}>Phone Number</label>
              <input type="tel" value={phone} onChange={e=>{
                const digits=e.target.value.replace(/\D/g,"").slice(0,10);
                let formatted=digits;
                if(digits.length>6)formatted=`(${digits.slice(0,3)}) ${digits.slice(3,6)}-${digits.slice(6)}`;
                else if(digits.length>3)formatted=`(${digits.slice(0,3)}) ${digits.slice(3)}`;
                else if(digits.length>0)formatted=`(${digits}`;
                setPhone(formatted);
              }} placeholder="(314) 555-0000" style={{width:"100%",padding:"11px 14px",background:T.inputBg,border:`1px solid ${T.border}`,borderRadius:3,color:T.text,fontSize:13,fontFamily:font.body,outline:"none"}}/>
            </div>
            {saved&&<div style={{fontSize:12,color:T.success,marginBottom:12,display:"flex",alignItems:"center",gap:6}}><I name="check" size={14} color={T.success}/>Changes saved successfully.</div>}
            <Btn primary icon="check" onClick={save} T={T}>Save Changes</Btn>
          </>}

          {activeTab==="notifications"&&<>
            <h3 style={{fontFamily:font.display,fontSize:20,fontWeight:600,color:T.cream,fontStyle:"italic",marginBottom:6}}>Notification Preferences</h3>
            <p style={{fontSize:12,color:T.textMuted,marginBottom:24,lineHeight:1.6}}>Control which notifications you receive and through which channels.</p>

            <div style={{fontSize:11,fontWeight:700,color:T.textDim,letterSpacing:".1em",textTransform:"uppercase",marginBottom:12}}>Email Notifications</div>
            {[
              {l:"New Messages",sub:"When Espion sends you a message",v:notifyMessages,set:setNotifyMessages},
              {l:"Approval Requests",sub:"When your input is required to proceed",v:notifyApprovals,set:setNotifyApprovals},
              {l:"Project Updates",sub:"When progress is posted on your projects",v:notifyEmail,set:setNotifyEmail},
              {l:"Invoices",sub:"When a new invoice is issued",v:notifyEmail,set:setNotifyEmail},
            ].map(n=>(
              <div key={n.l} style={{display:"flex",justifyContent:"space-between",alignItems:"center",padding:"14px 0",borderBottom:`1px solid ${T.border}`}}>
                <div>
                  <div style={{fontSize:13,fontWeight:600,color:T.cream}}>{n.l}</div>
                  <div style={{fontSize:12,color:T.textMuted,marginTop:2}}>{n.sub}</div>
                </div>
                <Toggle value={n.v} onChange={n.set}/>
              </div>
            ))}

            <div style={{fontSize:11,fontWeight:700,color:T.textDim,letterSpacing:".1em",textTransform:"uppercase",margin:"20px 0 12px"}}>In-App Notifications</div>
            <div style={{display:"flex",justifyContent:"space-between",alignItems:"center",padding:"14px 0",borderBottom:`1px solid ${T.border}`}}>
              <div>
                <div style={{fontSize:13,fontWeight:600,color:T.cream}}>Portal Notifications</div>
                <div style={{fontSize:12,color:T.textMuted,marginTop:2}}>Show notification badges in the portal sidebar</div>
              </div>
              <Toggle value={true} onChange={()=>{}}/>
            </div>

            <div style={{marginTop:20}}>
              {saved&&<div style={{fontSize:12,color:T.success,marginBottom:12,display:"flex",alignItems:"center",gap:6}}><I name="check" size={14} color={T.success}/>Preferences saved.</div>}
              <Btn primary icon="check" onClick={saveNotifications} T={T}>Save Preferences</Btn>
            </div>
          </>}

          {activeTab==="security"&&<>
            <h3 style={{fontFamily:font.display,fontSize:20,fontWeight:600,color:T.cream,fontStyle:"italic",marginBottom:24}}>Security Settings</h3>
            <div style={{padding:16,background:T.bg3,borderRadius:3,marginBottom:20}}>
              <div style={{display:"flex",alignItems:"center",gap:10,marginBottom:8}}>
                <I name="shield" size={16} color={T.success}/>
                <span style={{fontSize:13,fontWeight:600,color:T.cream}}>Account is secure</span>
              </div>
              <p style={{fontSize:12,color:T.textMuted}}>Your account is protected. Enable MFA for additional security.</p>
            </div>
            {passwordMsg&&<div style={{fontSize:12,color:passwordMsg.includes("Error")?T.danger:T.success,marginBottom:12,padding:"10px 14px",background:`${passwordMsg.includes("Error")?T.danger:T.success}10`,borderRadius:3}}>{passwordMsg}</div>}
            <Btn icon="lock" onClick={changePassword} T={T} style={{marginBottom:16}}>Send Password Reset Email</Btn>
            <p style={{fontSize:11,color:T.textDim,marginTop:8,lineHeight:1.5}}>You'll receive an email with a secure link to set a new password.</p>
            <Divider T={T}/>
            <h4 style={{fontSize:13,fontWeight:600,color:T.cream,marginBottom:8}}>Two-Factor Authentication</h4>
            <p style={{fontSize:12,color:T.textMuted,lineHeight:1.65,marginBottom:16}}>Add an extra layer of security with an authenticator app (Google Authenticator, Authy, etc.). When enabled, you'll need both your password and a verification code to sign in.</p>
            <MFAEnrollment T={T} supabase={supabase}/>
          </>}

          {activeTab==="preferences"&&<>
            <h3 style={{fontFamily:font.display,fontSize:20,fontWeight:600,color:T.cream,fontStyle:"italic",marginBottom:24}}>Appearance</h3>
            <div style={{marginBottom:24}}>
              <div style={{fontSize:11,fontWeight:600,color:T.textMuted,letterSpacing:".1em",textTransform:"uppercase",marginBottom:14}}>Portal Theme</div>
              <div style={{display:"grid",gridTemplateColumns:"1fr 1fr",gap:12}}>
                {[{id:"dark",label:"Dark Mode",desc:"Classic Espion aesthetic",icon:"moon"},{id:"light",label:"Light Mode",desc:"Clean and bright",icon:"sun"}].map(t=>(
                  <div key={t.id} onClick={()=>setTheme(t.id)} style={{padding:16,borderRadius:4,border:`2px solid ${theme===t.id?T.gold:T.border}`,cursor:"pointer",background:theme===t.id?`${T.gold}08`:T.bg3,transition:"all .2s"}}>
                    <div style={{display:"flex",alignItems:"center",gap:8,marginBottom:6}}>
                      <I name={t.icon} size={16} color={theme===t.id?T.gold:T.textMuted}/>
                      <span style={{fontSize:13,fontWeight:600,color:theme===t.id?T.gold:T.cream}}>{t.label}</span>
                      {theme===t.id&&<I name="check" size={13} color={T.gold}/>}
                    </div>
                    <div style={{fontSize:11,color:T.textMuted}}>{t.desc}</div>
                  </div>
                ))}
              </div>
            </div>
          </>}
        </Card>
      </div>
    </div>
  );
}


// ═══════════════════════════════════════════════════
// PASSWORD RESET SCREEN
// ═══════════════════════════════════════════════════
function PasswordResetScreen({T, onDone}) {
  const[password,setPassword]=useState("");
  const[confirm,setConfirm]=useState("");
  const[loading,setLoading]=useState(false);
  const[error,setError]=useState("");
  const[success,setSuccess]=useState(false);

  const handleReset = async () => {
    if(!password){setError("Please enter a new password.");return;}
    if(password.length<8){setError("Password must be at least 8 characters.");return;}
    if(password!==confirm){setError("Passwords do not match.");return;}
    setLoading(true);setError("");
    const{error:e}=await supabase.auth.updateUser({password});
    if(e){setError(e.message||"Failed to reset password.");setLoading(false);return;}
    setSuccess(true);
    setTimeout(()=>onDone(),2500);
    setLoading(false);
  };

  return(
    <div style={{minHeight:"100vh",background:T.bg,display:"flex",alignItems:"center",justifyContent:"center",fontFamily:font.body}}>
      <div style={{width:420,padding:48,background:T.bg2,border:`1px solid ${T.border}`,borderRadius:6,boxShadow:`0 24px 80px ${T.shadow}`}}>
        <div style={{marginBottom:32,textAlign:"center"}}>
          <EspionMark size={28} showText={true}/>
          <div style={{fontSize:11,color:"#a09070",letterSpacing:".14em",textTransform:"uppercase",marginTop:8}}>Set New Password</div>
        </div>
        {success?(
          <div style={{textAlign:"center",padding:16}}>
            <div style={{fontSize:40,marginBottom:12}}>✓</div>
            <div style={{fontSize:16,fontWeight:600,color:T.cream,marginBottom:8}}>Password updated!</div>
            <div style={{fontSize:13,color:T.textMuted}}>Redirecting you to the portal...</div>
          </div>
        ):(
          <>
            <p style={{fontSize:13,color:T.textMuted,marginBottom:24,lineHeight:1.6}}>Enter your new password below. It must be at least 8 characters.</p>
            <div style={{marginBottom:16}}>
              <label style={{display:"block",fontSize:10,fontWeight:600,letterSpacing:".1em",textTransform:"uppercase",color:T.textMuted,marginBottom:7}}>New Password</label>
              <input type="password" value={password} onChange={e=>setPassword(e.target.value)} placeholder="••••••••••" style={{width:"100%",padding:"11px 14px",background:T.inputBg,border:`1px solid ${T.border}`,borderRadius:3,color:T.text,fontSize:13,outline:"none"}}/>
            </div>
            <div style={{marginBottom:16}}>
              <label style={{display:"block",fontSize:10,fontWeight:600,letterSpacing:".1em",textTransform:"uppercase",color:T.textMuted,marginBottom:7}}>Confirm Password</label>
              <input type="password" value={confirm} onChange={e=>setConfirm(e.target.value)} placeholder="••••••••••" style={{width:"100%",padding:"11px 14px",background:T.inputBg,border:`1px solid ${T.border}`,borderRadius:3,color:T.text,fontSize:13,outline:"none"}} onKeyDown={e=>e.key==="Enter"&&handleReset()}/>
            </div>
            {error&&<div style={{fontSize:12,color:T.danger,marginBottom:16,padding:"10px 14px",background:`${T.danger}10`,borderRadius:3}}>{error}</div>}
            <button onClick={handleReset} disabled={loading} style={{width:"100%",padding:"13px",fontSize:12,fontWeight:600,letterSpacing:".08em",textTransform:"uppercase",color:T.bg,background:`linear-gradient(135deg,${T.gold},${T.goldDark})`,border:"none",borderRadius:2,cursor:loading?"not-allowed":"pointer",opacity:loading?.6:1}}>
              {loading?"Updating...":"Set New Password"}
            </button>
          </>
        )}
      </div>
    </div>
  );
}

// ═══════════════════════════════════════════════════
// MAIN APP
// ═══════════════════════════════════════════════════
export default function EspionPortal(){
  const[themeMode,setThemeMode]=useState(()=>{
    try{return localStorage.getItem("espion_portal_theme")||"dark";}catch{return "dark";}
  });
  const[authUser,setAuthUser]=useState(null);
  const[loading,setLoading]=useState(true);
  const[isPasswordReset,setIsPasswordReset]=useState(false);
  const[section,setSection]=useState(()=>{
    try{return localStorage.getItem("espion_portal_section")||"dashboard";}catch{return "dashboard";}
  });
  const[focusProjectId,setFocusProjectId]=useState(null);
  const[focusMessageProjectId,setFocusMessageProjectId]=useState(null);
  const[collapsed,setCollapsed]=useState(false);

  const{profile}=useProfile(authUser?.id);
  const{projects,submitApproval}=useProjects(authUser?.id);
  const{messages,sendMessage}=useMessages(authUser?.id);
  const{invoices}=useInvoices(authUser?.id);
  const{resources}=useResources();

  const user=profile||(authUser?{...MOCK_USER,name:authUser.user_metadata?.full_name||authUser.email,email:authUser.email,id:authUser.id}:MOCK_USER);
  const activeProjects=authUser?(projects.length>0?projects:[]):MOCK_PROJECTS;
  const activeMessages=authUser?(messages.length>0?messages:[]):MOCK_MESSAGES;
  const activeInvoices=authUser?(invoices.length>0?invoices:[]):MOCK_INVOICES;
  const activeResources=resources.length>0?resources:MOCK_RESOURCES;

  const T=themeMode==="dark"?dark:light;

  useEffect(()=>{
    try{localStorage.setItem("espion_portal_theme",themeMode);}catch{}
  },[themeMode]);

  useEffect(()=>{
    try{localStorage.setItem("espion_portal_section",section);}catch{}
  },[section]);

  useEffect(()=>{
    supabase.auth.getSession().then(({data:{session}})=>{
      if(session?.user)setAuthUser(session.user);
      setLoading(false);
    }).catch(()=>setLoading(false));

    const{data:{subscription}}=supabase.auth.onAuthStateChange((event,session)=>{
      if(event==="PASSWORD_RECOVERY"){setIsPasswordReset(true);setAuthUser(session?.user||null);}
      else{setAuthUser(session?.user||null);}
    });
    return()=>subscription.unsubscribe();
  },[]);

  const handleSignOut=async()=>{
    await supabase.auth.signOut().catch(()=>{});
    setAuthUser(null);
    setSection("dashboard");
    try{localStorage.removeItem("espion_portal_section");}catch{}
  };

  // Mark messages as read when designer views the Messages section
  useEffect(()=>{
    if(section!=="messages"||!authUser)return;
    const ids=activeProjects.map(p=>p.id).filter(Boolean);
    if(!ids.length)return;
    supabase.from("messages").update({read:true})
      .eq("is_espion",true)
      .eq("read",false)
      .in("project_id",ids)
      .then(()=>{
        // Force badge to zero immediately in local state
        if(typeof activeMessages !== "undefined"){
          activeMessages.forEach(m=>{if(m.is_espion||m.isEspion)m.read=true;});
        }
      });
  },[section,authUser,activeProjects.length]);

  const navigate=(dest,id)=>{
    if(dest==="projects"){setSection("projects");if(id)setFocusProjectId(id);}
    else if(dest==="messages"){setSection("messages");if(id)setFocusMessageProjectId(id);}
    else setSection(dest);
  };

  // Global CSS
  const css=`
    *{margin:0;padding:0;box-sizing:border-box}
    body{background:${T.bg};color:${T.text};font-family:'DM Sans','Helvetica Neue',sans-serif}
    ::-webkit-scrollbar{width:5px;height:5px}
    ::-webkit-scrollbar-track{background:${T.bg}}
    ::-webkit-scrollbar-thumb{background:${T.border};border-radius:3px}
    input,textarea,select{color-scheme:${themeMode}}
    button{font-family:'DM Sans','Helvetica Neue',sans-serif}
    @keyframes fadeUp{from{opacity:0;transform:translateY(16px)}to{opacity:1;transform:translateY(0)}}
    .fade-in{animation:fadeUp .4s cubic-bezier(.23,1,.32,1) both}
  `;

  if(loading)return(
    <div style={{minHeight:"100vh",background:dark.bg,display:"flex",alignItems:"center",justifyContent:"center"}}>
      <style>{`body{background:${dark.bg}}`}</style>
      <div style={{textAlign:"center"}}>
        <EspionMark size={48} showText={true} style={{margin:"0 auto"}}/>
        <div style={{fontSize:11,color:dark.textDim,marginTop:6,letterSpacing:".12em",textTransform:"uppercase"}}>Loading portal...</div>
      </div>
    </div>
  );

  if(isPasswordReset)return(
    <>
      <style>{css}</style>
      <PasswordResetScreen T={T} onDone={()=>{setIsPasswordReset(false);}}/>
    </>
  );

  if(!authUser)return(
    <>
      <style>{css}</style>
      <AuthScreen T={T} onAuth={u=>setAuthUser(u)}/>
    </>
  );

  const nav=[
    {id:"dashboard",label:"Dashboard",icon:"home"},
    {id:"projects",label:"Projects",icon:"folder"},
    {id:"messages",label:"Messages",icon:"message"},
    {id:"files",label:"Files",icon:"file"},
    {id:"invoices",label:"Invoices",icon:"dollar"},
    {id:"resources",label:"Resources",icon:"book"},
    {id:"settings",label:"Settings",icon:"settings"},
  ];

  const pendingCount=activeProjects.flatMap(p=>p.pendingApprovals||[]).filter(a=>a.status==="pending").length;
  // Count unread messages from Espion (is_espion=true that designer hasn't read)
  const unreadCount=activeMessages.filter(m=>(m.is_espion||m.isEspion)&&!m.read).length;

  return(
    <div style={{display:"flex",minHeight:"100vh",background:T.bg,fontFamily:font.body}}>
      <style>{css}</style>

      {/* Sidebar */}
      <aside style={{width:collapsed?64:240,minWidth:collapsed?64:240,background:T.sidebarBg,borderRight:`1px solid ${T.border}`,display:"flex",flexDirection:"column",transition:"width .3s cubic-bezier(.23,1,.32,1),min-width .3s cubic-bezier(.23,1,.32,1)",position:"sticky",top:0,height:"100vh",zIndex:10}}>
        <div style={{padding:collapsed?"14px 16px":"20px 20px",borderBottom:`1px solid ${T.border}`,display:"flex",alignItems:"center",justifyContent:collapsed?"center":"flex-start"}}>
          <Logo T={T} collapsed={collapsed}/>
        </div>

        <nav style={{flex:1,padding:"12px 8px",overflowY:"auto"}}>
          {nav.map(n=>{
            const active=section===n.id;
            const badge=n.id==="projects"&&pendingCount>0?pendingCount:n.id==="messages"&&unreadCount>0?unreadCount:null;
            return(
              <div key={n.id} onClick={()=>{setSection(n.id);setFocusProjectId(null);setFocusMessageProjectId(null);}} style={{display:"flex",alignItems:"center",gap:10,padding:collapsed?"10px 0":"10px 12px",marginBottom:2,borderRadius:3,cursor:"pointer",justifyContent:collapsed?"center":"flex-start",background:active?`${T.gold}12`:"transparent",borderRight:active?`2px solid ${T.gold}`:"2px solid transparent",transition:"all .2s",position:"relative"}} onMouseEnter={e=>{if(!active)e.currentTarget.style.background=`${T.gold}06`}} onMouseLeave={e=>{if(!active)e.currentTarget.style.background="transparent"}}>
                <I name={n.icon} size={17} color={active?T.gold:T.textDim}/>
                {!collapsed&&<span style={{fontSize:12,fontWeight:active?600:400,color:active?T.cream:T.textMuted,letterSpacing:".02em",flex:1}}>{n.label}</span>}
                {!collapsed&&badge&&<div style={{width:18,height:18,borderRadius:"50%",background:T.danger,display:"flex",alignItems:"center",justifyContent:"center",fontSize:9,fontWeight:700,color:"#fff"}}>{badge}</div>}
                {collapsed&&badge&&<div style={{position:"absolute",top:6,right:6,width:8,height:8,borderRadius:"50%",background:T.danger}}/>}
              </div>
            );
          })}
        </nav>

        {/* User profile at bottom */}
        <div style={{padding:"12px 8px",borderTop:`1px solid ${T.border}`}}>
          {!collapsed&&<div style={{display:"flex",alignItems:"center",gap:10,padding:"10px 12px",marginBottom:8,borderRadius:3,background:T.bg3}}>
            <Avatar initials={(user?.full_name||user?.name||user?.email||"D").charAt(0).toUpperCase()} size={32} T={T}/>
            <div style={{flex:1,minWidth:0}}>
              <div style={{fontSize:12,fontWeight:600,color:T.cream,overflow:"hidden",textOverflow:"ellipsis",whiteSpace:"nowrap"}}>{user?.full_name||user?.name||"Designer"}</div>
              <div style={{fontSize:10,color:T.textDim,overflow:"hidden",textOverflow:"ellipsis",whiteSpace:"nowrap"}}>{user?.firm||"Design Firm"}</div>
            </div>
          </div>}

          {/* Theme toggle */}
          <div onClick={()=>setThemeMode(m=>m==="dark"?"light":"dark")} style={{display:"flex",alignItems:"center",gap:8,padding:collapsed?"10px 0":"8px 12px",borderRadius:3,cursor:"pointer",justifyContent:collapsed?"center":"flex-start",marginBottom:4}} onMouseEnter={e=>e.currentTarget.style.background=T.bg3} onMouseLeave={e=>e.currentTarget.style.background="transparent"}>
            <I name={themeMode==="dark"?"sun":"moon"} size={15} color={T.textDim}/>
            {!collapsed&&<span style={{fontSize:11,color:T.textMuted}}>{themeMode==="dark"?"Light Mode":"Dark Mode"}</span>}
          </div>

          <div onClick={()=>setCollapsed(!collapsed)} style={{display:"flex",alignItems:"center",gap:8,padding:collapsed?"10px 0":"8px 12px",borderRadius:3,cursor:"pointer",justifyContent:collapsed?"center":"flex-start"}} onMouseEnter={e=>e.currentTarget.style.background=T.bg3} onMouseLeave={e=>e.currentTarget.style.background="transparent"}>
            <I name={collapsed?"arrowR":"arrowL"} size={14} color={T.textDim}/>
            {!collapsed&&<span style={{fontSize:11,color:T.textMuted}}>Collapse sidebar</span>}
          </div>
        </div>
      </aside>

      {/* Main content */}
      <main style={{flex:1,overflow:"auto",background:T.bg,minWidth:0}}>
        {/* Top bar */}
        <header style={{position:"sticky",top:0,zIndex:9,display:"flex",alignItems:"center",justifyContent:"space-between",padding:"14px 32px",background:`${T.bg}f0`,borderBottom:`1px solid ${T.border}`,backdropFilter:"blur(20px)"}}>
          <div>
            <h1 style={{fontFamily:font.display,fontSize:18,fontWeight:500,color:T.cream,fontStyle:"italic"}}>{nav.find(n=>n.id===section)?.label}</h1>
            <p style={{fontSize:10,color:T.textDim,letterSpacing:".06em",textTransform:"uppercase",marginTop:1}}>Espion Furniture Co. — Designer Portal</p>
          </div>
          <div style={{display:"flex",gap:12,alignItems:"center"}}>
            <Badge color={T.gold} filled T={T}>Active Partner</Badge>
            <div style={{display:"flex",alignItems:"center",gap:8,padding:"6px 12px",background:T.bg3,borderRadius:3,border:`1px solid ${T.border}`,cursor:"pointer"}} onClick={()=>setSection("settings")}>
              <Avatar initials={(user?.full_name||user?.name||user?.email||"D").charAt(0).toUpperCase()} size={24} T={T}/>
              {!collapsed&&<span style={{fontSize:12,color:T.text}}>{user?.full_name||user?.name||"Designer"}</span>}
            </div>
          </div>
        </header>

        <div style={{padding:32}} className="fade-in">
          {section==="dashboard"&&<Dashboard user={user} projects={activeProjects} messages={activeMessages} invoices={activeInvoices} T={T} onNavigate={navigate}/>}
          {section==="projects"&&<Projects projects={activeProjects} T={T} focusId={focusProjectId} onFocusId={setFocusProjectId} onSubmitApproval={submitApproval} user={user}/>}
          {section==="messages"&&<Messages projects={activeProjects} messages={activeMessages} T={T} focusProjectId={focusMessageProjectId} onSend={sendMessage} userId={authUser?.id} userName={user?.full_name||user?.name} userInitial={(user?.full_name||user?.name||"U").charAt(0)}/>}
          {section==="files"&&<Files projects={activeProjects} T={T} userId={authUser?.id}/>}
          {section==="invoices"&&<Invoices invoices={activeInvoices} T={T}/>}
          {section==="resources"&&<Resources T={T} resources={activeResources}/>}
          {section==="settings"&&<Settings user={user} T={T} theme={themeMode} setTheme={setThemeMode} onSignOut={handleSignOut}/>}
        </div>
      </main>
    </div>
  );
}
